import { sql, relations } from "drizzle-orm";
import { pgTable, text, varchar, integer, timestamp, boolean, jsonb, real, index, uniqueIndex, serial, unique, doublePrecision, date } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").unique(),
  password: text("password").notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull().unique(),
  grade: integer("grade").notNull().default(6),
  school: text("school"),
  gender: text("gender"), // male, female, other, prefer_not_to_say
  dateOfBirth: timestamp("date_of_birth"), // for age-based campaign targeting
  role: text("role").notNull().default("student"), // student, admin
  profilePhotoUrl: text("profile_photo_url"), // URL to profile photo in object storage
  bio: text("bio"),
  profilePhotoBonusAwarded: boolean("profile_photo_bonus_awarded").default(false), // true if user received 25 XP bonus for uploading photo
  
  // Parent/Guardian Information
  parentFirstName: text("parent_first_name"),
  parentLastName: text("parent_last_name"),
  parentEmail: text("parent_email"),
  parentPhone: text("parent_phone"),
  whatsappVerified: boolean("whatsapp_verified").default(false),
  whatsappReportOptIn: boolean("whatsapp_report_opt_in").default(false),
  whatsappReportLastSent: timestamp("whatsapp_report_last_sent"),

  // Signup attribution (UTM / ref params captured on /signup and the paid landing pages).
  // utm_content, utm_term and fbclid were added for the Meta campaign: content and term separate
  // one creative from another inside a single campaign, and fbclid is the only way to tie a
  // registration back to a click when Meta's _fbc cookie never got written (the Pixel was blocked,
  // or loaded after a redirect had already dropped the parameter).
  signupSource: text("signup_source"),
  signupUtmSource: text("signup_utm_source"),
  signupUtmMedium: text("signup_utm_medium"),
  signupUtmCampaign: text("signup_utm_campaign"),
  signupUtmContent: text("signup_utm_content"),
  signupUtmTerm: text("signup_utm_term"),
  signupFbclid: text("signup_fbclid"),
  signupLandingPath: text("signup_landing_path"),
  
  // Stripe subscription fields
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  cardFingerprint: text("card_fingerprint"), // fingerprint of the payment method on file; used to warn on duplicate-card signups across accounts
  subscriptionStatus: text("subscription_status").default("inactive"), // active, inactive, expired, cancelled, trial, past_due, paused
  subscriptionPlan: text("subscription_plan"), // monthly, yearly
  subscriptionEndDate: timestamp("subscription_end_date"),
  subscriptionPausedUntil: timestamp("subscription_paused_until"), // set when subscription is paused; null when not paused
  subscribedGrade: integer("subscribed_grade"), // which grade they paid for
  subscriptionCancelAtPeriodEnd: boolean("subscription_cancel_at_period_end").default(false), // true if subscription is set to cancel
  paymentFailedAt: timestamp("payment_failed_at"), // set when payment first fails; used for grace period + dunning sequence
  dunningLastDay: integer("dunning_last_day").default(0), // last dunning day processed (1,2,3,7,14,30) to avoid duplicate sends
  scheduledReferralSendAt: timestamp("scheduled_referral_send_at"), // set when subscription activates; checked by cron to fire 7-day referral email

  // Apple in-app subscription (StoreKit 2, POST /api/mobile/v1/subscription/apple).
  // subscriptionProvider: "apple" once an App Store transaction is bound; null/absent for Stripe.
  subscriptionProvider: text("subscription_provider"),
  appleOriginalTransactionId: text("apple_original_transaction_id"),
  // Google Play subscription (POST /api/mobile/v1/subscription/google); the purchase token is the binding key.
  googlePurchaseToken: text("google_purchase_token"),
  
  // Onboarding for migrated subscribers
  onboardingToken: text("onboarding_token").unique(), // unique token for onboarding link
  onboardingCompleted: boolean("onboarding_completed").default(false), // true when user finishes onboarding

  // One-time grade confirmation prompt. Null = user hasn't confirmed their September grade yet.
  gradeConfirmedAt: timestamp("grade_confirmed_at"),

  isTestAccount: boolean("is_test_account").default(false),
  
  accessibilityPreferences: jsonb("accessibility_preferences"),
  uiPreferences: jsonb("ui_preferences"), // cross-device synced UI state: theme, adminFavourites

  // Web push notification preferences (per category, all on by default)
  pushMessages: boolean("push_messages").default(true).notNull(),
  pushNotifications: boolean("push_notifications").default(true).notNull(),
  pushReminders: boolean("push_reminders").default(true).notNull(),

  // Retention offer tracking — each offer is shown at most once per account
  retentionSaveOfferShown: boolean("retention_save_offer_shown").default(false).notNull(),
  retentionPauseOfferShown: boolean("retention_pause_offer_shown").default(false).notNull(),
  retentionWinbackSent: boolean("retention_winback_sent").default(false).notNull(),
  
  forcePasswordReset: boolean("force_password_reset").default(false).notNull(),

  // OpenEMIS provisioning — set when the account was created (or later
  // linked) by an OpenEMIS push through /api/openemis/webservice.
  // openemisNo is unique WITHIN a single OpenEMIS connection, not
  // globally, so uniqueness is enforced by a composite index on
  // openemisConnections + openemisNo (see openemisUserLinkIdx below).
  openemisNo: text("openemis_no"),
  openemisConnectionId: varchar("openemis_connection_id"),
  openemisSyncedAt: timestamp("openemis_synced_at"),

  // Account deletion (15-day grace period)
  deletionRequestedAt: timestamp("deletion_requested_at"),
  deletionReason: text("deletion_reason"),
  // Opt-out from the public all-time leaderboard. Default false: even
  // deleted/cancelled users remain as Hall of Fame entries (anonymized to
  // first name + last initial when deletion_requested_at is set).
  excludeFromPublicLeaderboard: boolean("exclude_from_public_leaderboard").default(false).notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastActiveAt: timestamp("last_active_at").defaultNow().notNull(),
}, (table) => ({
  // Partial unique index: OpenEMIS-provisioned users are unique per
  // (connection, openemis_no) pair. Rows without an openemis_no (i.e.
  // every non-OpenEMIS user) are excluded from the constraint so they
  // don't clash on NULL.
  openemisConnectionNoUniq: uniqueIndex("users_openemis_connection_no_uniq")
    .on(table.openemisConnectionId, table.openemisNo)
    .where(sql`${table.openemisNo} IS NOT NULL`),
  // Ownership lookup for Apple transactions (one originalTransactionId belongs to one account).
  appleOriginalTransactionIdx: index("users_apple_original_transaction_id_idx")
    .on(table.appleOriginalTransactionId)
    .where(sql`${table.appleOriginalTransactionId} IS NOT NULL`),
}));

// User Progress Tracking
export const userProgress = pgTable("user_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  subject: text("subject").notNull(), // mathematics, science, etc
  topic: text("topic").notNull(), // number, measurement, etc
  totalQuestions: integer("total_questions").notNull().default(0),
  correctAnswers: integer("correct_answers").notNull().default(0),
  totalPoints: integer("total_points").notNull().default(0),
  lastStudiedAt: timestamp("last_studied_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}, (table) => ({
  // Unique index on (user_id, subject, topic) for atomic upserts
  userSubjectTopicIdx: uniqueIndex("user_progress_user_subject_topic_idx").on(table.userId, table.subject, table.topic),
}));

// Individual Question Attempts
export const questionAttempts = pgTable("question_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  questionId: text("question_id").notNull(),
  subject: text("subject").notNull(), // mathematics, science, etc
  topic: text("topic").notNull(),
  difficulty: text("difficulty").notNull().default("easy"), // easy, medium, hard
  selectedAnswer: text("selected_answer").notNull(),
  correctAnswer: text("correct_answer").notNull(),
  isCorrect: boolean("is_correct").notNull(),
  basePoints: integer("base_points").notNull().default(100), // base points before multipliers
  pointsEarned: integer("points_earned").notNull().default(0), // actual points after anti-grind
  timeSpent: integer("time_spent"), // in seconds
  // Set by the mobile app so an answer recorded offline and replayed later is stored once,
  // however many times the replay is retried. Unique per user (partial index in the migration).
  clientAttemptId: text("client_attempt_id"),
  // True when the student answered a question from a grade other than their own (review mode).
  // Review attempts are recorded for the parent report but earn no XP/points and are excluded
  // from every leaderboard, streak, badge and prize calculation. Derived server-side from the
  // question's grade vs the user's grade, so a client cannot farm lower-grade questions.
  isReview: boolean("is_review").notNull().default(false),
  attemptedAt: timestamp("attempted_at").defaultNow().notNull(),
}, (table) => ({
  // Leaderboards, progress and reports all scan attempts by user and recency;
  // the question index backs stats aggregation. Created CONCURRENTLY on prod/dev 2026-09-03.
  userAttemptedIdx: index("question_attempts_user_attempted_idx").on(table.userId, table.attemptedAt),
  questionIdx: index("question_attempts_question_idx").on(table.questionId),
}));

// Legacy EduFocal (edufocalprod1) accounts staged for claim-on-login.
// Rows are loaded by scripts/import-legacy-users.ts; a row is "claimed" when a login with the
// legacy email + password creates (or links) an Academy user. Never exposed to students directly.
export const legacyUsers = pgTable("legacy_users", {
  legacyId: integer("legacy_id").primaryKey(),
  email: text("email").notNull(),                 // lowercased
  username: text("username"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  parentEmail: text("parent_email"),
  parentFirstName: text("parent_first_name"),
  parentLastName: text("parent_last_name"),
  passwordHash: text("password_hash"),            // legacy bcrypt ($2y$/$2a$), verified with node bcrypt
  role: text("role").notNull().default("student"),
  examLevel: text("exam_level"),                  // GSAT / GR4 / GR5 / CXC ...
  grade: integer("grade"),                        // Academy grade derived from exam_level
  school: text("school"),
  parish: text("parish"),
  legacyCreatedAt: timestamp("legacy_created_at"),
  legacyLastLogin: timestamp("legacy_last_login"),
  summary: jsonb("summary"),                      // { tests, finishedTests, answers, correct, firstActivity, lastActivity, topics: [{subject, topic, attempts, correct}] }
  claimedBy: varchar("claimed_by").references(() => users.id, { onDelete: "set null" }),
  claimedAt: timestamp("claimed_at"),
  importedAt: timestamp("imported_at").defaultNow().notNull(),
}, (table) => ({
  emailIdx: index("legacy_users_email_idx").on(table.email),
  claimedByIdx: index("legacy_users_claimed_by_idx").on(table.claimedBy),
}));
export type LegacyUser = typeof legacyUsers.$inferSelect;
export type InsertLegacyUser = typeof legacyUsers.$inferInsert;

// User Subject Statistics (for anti-grinding)
export const userSubjectStats = pgTable("user_subject_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  subject: text("subject").notNull(),
  totalPoints: integer("total_points").notNull().default(0),
  totalQuestions: integer("total_questions").notNull().default(0),
  correctAnswers: integer("correct_answers").notNull().default(0),
  fatigueScore: real("fatigue_score").notNull().default(0), // 0-1, higher = more fatigued
  recentActivity: jsonb("recent_activity").default("[]"), // rolling 7-day activity window
  lastAwardedAt: timestamp("last_awarded_at").defaultNow().notNull(),
  weeklyQuestsCompleted: integer("weekly_quests_completed").notNull().default(0),
  currentStreak: integer("current_streak").notNull().default(0),
  bestStreak: integer("best_streak").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Topic Streaks and Progression
export const userTopicStreaks = pgTable("user_topic_streaks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  subject: text("subject").notNull(),
  topic: text("topic").notNull(),
  currentStreak: integer("current_streak").notNull().default(0),
  bestStreak: integer("best_streak").notNull().default(0),
  lastDifficulty: text("last_difficulty").notNull().default("easy"),
  masteryLevel: real("mastery_level").notNull().default(0), // 0-1
  lastAttemptAt: timestamp("last_attempt_at").defaultNow().notNull(),
});

// User Levels and XP
export const userLevels = pgTable("user_levels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  currentLevel: integer("current_level").notNull().default(1),
  totalXP: integer("total_xp").notNull().default(0),
  levelXP: integer("level_xp").notNull().default(0), // XP in current level
  nextLevelXP: integer("next_level_xp").notNull().default(300), // XP needed for next level
  testsCompleted: integer("tests_completed").notNull().default(0), // Total tests completed (for prize milestones)
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Leaderboard Snapshots (for ranking decay)
export const leaderboardSnapshots = pgTable("leaderboard_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  snapshotDate: timestamp("snapshot_date").notNull(),
  totalEffectivePoints: integer("total_effective_points").notNull().default(0),
  activityFactor: real("activity_factor").notNull().default(1.0), // decay multiplier
  finalScore: integer("final_score").notNull().default(0), // points * activity factor
  inactiveDays: integer("inactive_days").notNull().default(0),
  rank: integer("rank"),
});

// User Achievements/Badges
export const userAchievements = pgTable("user_achievements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  achievementType: text("achievement_type").notNull(), // first_correct, streak_5, topic_master, etc
  achievementData: jsonb("achievement_data"), // flexible data for different achievement types
  earnedAt: timestamp("earned_at").defaultNow().notNull(),
});

// Session Management (managed by connect-pg-simple)
export const userSessions = pgTable("user_sessions", {
  sid: varchar("sid").primaryKey(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire", { precision: 6, mode: 'string' }).notNull(),
});

// Conversations between users
export const conversations = pgTable("conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  user1Id: varchar("user1_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  user2Id: varchar("user2_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lastMessageAt: timestamp("last_message_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Messages between users
export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  senderId: varchar("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  recipientId: varchar("recipient_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Password Reset Tokens
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  token: text("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  used: boolean("used").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Questions Database
export const questions = pgTable("questions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(), // 4, 5, 6
  subject: text("subject").notNull(),
  exam: text("exam"), // PEP | CSEC | CAPE
  paper: text("paper"), // paper1 (multiple choice) | paper2 | paper3
  module: integer("module"), // CAPE only // mathematics, science, english
  topic: text("topic").notNull(), // number, measurement, geometry, etc
  questionType: text("question_type").notNull().default("multiple-choice"), // multiple-choice, multi-select, true-false, short-answer, fill-blank, order-match, table-grid, matching
  questionText: text("question_text").notNull(),
  questionImageUrl: text("question_image_url"), // URL/path to question image
  questionMath: text("question_math"), // LaTeX mathematical expressions in question
  options: jsonb("options"), // For multiple choice: [{"text": "Answer A", "imageUrl": "url", "math": "latex"}]
  correctAnswer: text("correct_answer").notNull(), // For multi-select: JSON array like ["A","C"] or option IDs
  explanation: text("explanation"),
  explanationImageUrl: text("explanation_image_url"), // URL/path to explanation image
  explanationMath: text("explanation_math"), // LaTeX mathematical expressions in explanation
  aiExplanation: text("ai_explanation"), // AI-generated age-appropriate explanation
  difficulty: text("difficulty").notNull().default("neutral"), // neutral, easy, medium, hard (auto-calculated from stats)
  points: integer("points").notNull().default(100), // auto-calculated based on difficulty
  managedDifficulty: boolean("managed_difficulty").notNull().default(true), // if true, difficulty/points auto-managed by system
  tags: jsonb("tags").default("[]"), // ["fractions", "word-problems"] etc
  createdBy: varchar("created_by").references(() => users.id), // admin who created
  generationMethod: text("generation_method").notNull().default("manual"), // manual, ai, import, quizzative
  status: text("status").notNull().default("sandbox"), // sandbox, ready, published
  reviewNotes: text("review_notes"), // admin feedback for questions in sandbox/ready state
  isActive: boolean("is_active").notNull().default(true),
  hint: text("hint"),
  quizzativeId: text("quizzative_id").unique(),
  quizzativeQuizId: text("quizzative_quiz_id"),
  quizzativeData: jsonb("quizzative_data"),
  subTopic: text("sub_topic"), // NSC/CSEC sub-topic for term-based study paths
  groupId: varchar("group_id"),
  groupOrder: integer("group_order").default(0),
  groupStimulus: text("group_stimulus"),
  archived: boolean("archived").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Term-topic mappings for study-by-term feature
export const termTopicMappings = pgTable("term_topic_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(),
  subject: text("subject").notNull(),
  topic: text("topic").notNull(),
  subTopic: text("sub_topic").notNull(),
  term: integer("term").notNull(), // 1, 2, or 3
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Question Statistics for Adaptive Learning
export const questionStats = pgTable("question_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }).unique(),
  attempts: integer("attempts").notNull().default(0), // total attempts
  correct: integer("correct").notNull().default(0), // correct answers
  pCorrect: real("p_correct").notNull().default(0.5), // probability of correctness (Beta-smoothed)
  ewmaCorrect: real("ewma_correct").notNull().default(0.5), // exponentially weighted moving average
  difficulty: text("difficulty").notNull().default("neutral"), // neutral, easy, medium, hard
  points: integer("points").notNull().default(100), // calculated points
  exposureCount: integer("exposure_count").notNull().default(0), // how many times shown
  lastAttemptAt: timestamp("last_attempt_at"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Question Objectives - Learning objectives linked to questions (from Quizzative)
export const questionObjectives = pgTable("question_objectives", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }),
  objectiveCode: text("objective_code").notNull(),
  objectiveDescription: text("objective_description").notNull(),
  standardName: text("standard_name"),
  topicName: text("topic_name"),
  topicDescription: text("topic_description"),
  periodName: text("period_name"),
  periodShortCode: text("period_short_code"),
  quizzativeObjectiveId: text("quizzative_objective_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  questionIdIdx: index("question_objectives_question_id_idx").on(table.questionId),
  objectiveCodeIdx: index("question_objectives_code_idx").on(table.objectiveCode),
}));

// Quizzative Sync Log - Track sync operations
export const quizzativeSyncLog = pgTable("quizzative_sync_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  syncedBy: varchar("synced_by").references(() => users.id),
  status: text("status").notNull().default("running"), // running, completed, failed
  totalQuizzes: integer("total_quizzes").notNull().default(0),
  questionsCreated: integer("questions_created").notNull().default(0),
  questionsUpdated: integer("questions_updated").notNull().default(0),
  questionsSkipped: integer("questions_skipped").notNull().default(0),
  objectivesLinked: integer("objectives_linked").notNull().default(0),
  errors: jsonb("errors").default("[]"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

// Question Change Log - Track field-level changes from Quizzative syncs
export const questionChangeLogs = pgTable("question_change_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }),
  quizzativeId: text("quizzative_id").notNull(),
  syncLogId: varchar("sync_log_id").references(() => quizzativeSyncLog.id, { onDelete: "set null" }),
  changeType: text("change_type").notNull(), // created, updated
  changedFields: jsonb("changed_fields"), // { fieldName: { old: "...", new: "..." } }
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  questionIdIdx: index("question_change_logs_question_id_idx").on(table.questionId),
  quizzativeIdIdx: index("question_change_logs_quizzative_id_idx").on(table.quizzativeId),
  syncLogIdIdx: index("question_change_logs_sync_log_id_idx").on(table.syncLogId),
}));

// Quizzative Standards Database - Normalized curriculum hierarchy mirroring Quizzative
export const quizzativeStandards = pgTable("quizzative_standards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  quizzativeId: text("quizzative_id").notNull().unique(),
  name: text("name").notNull(),
  derivedGrade: integer("derived_grade"),
  lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  quizzativeIdIdx: index("qz_standards_quizzative_id_idx").on(table.quizzativeId),
}));

export const quizzativeTopics = pgTable("quizzative_topics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  quizzativeId: text("quizzative_id").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  standardId: varchar("standard_id").references(() => quizzativeStandards.id, { onDelete: "cascade" }),
  lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  quizzativeIdIdx: index("qz_topics_quizzative_id_idx").on(table.quizzativeId),
  standardIdIdx: index("qz_topics_standard_id_idx").on(table.standardId),
}));

export const quizzativeObjectives = pgTable("quizzative_objectives", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  quizzativeId: text("quizzative_id").notNull().unique(),
  code: text("code").notNull(),
  description: text("description").notNull(),
  topicId: varchar("topic_id").references(() => quizzativeTopics.id, { onDelete: "cascade" }),
  standardId: varchar("standard_id").references(() => quizzativeStandards.id, { onDelete: "cascade" }),
  periodName: text("period_name"),
  periodShortCode: text("period_short_code"),
  lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  quizzativeIdIdx: index("qz_objectives_quizzative_id_idx").on(table.quizzativeId),
  topicIdIdx: index("qz_objectives_topic_id_idx").on(table.topicId),
  standardIdIdx: index("qz_objectives_standard_id_idx").on(table.standardId),
  codeIdx: index("qz_objectives_code_idx").on(table.code),
}));

// Quizzative Export Sync Log - Track export API sync operations (separate from OAuth sync)
export const quizzativeExportSyncLog = pgTable("quizzative_export_sync_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  syncedBy: varchar("synced_by").references(() => users.id),
  status: text("status").notNull().default("running"),
  totalQuestions: integer("total_questions").notNull().default(0),
  questionsCreated: integer("questions_created").notNull().default(0),
  questionsUpdated: integer("questions_updated").notNull().default(0),
  questionsSkipped: integer("questions_skipped").notNull().default(0),
  objectivesLinked: integer("objectives_linked").notNull().default(0),
  filters: jsonb("filters"),
  errors: jsonb("errors").default("[]"),
  lastSince: text("last_since"),
  exportedAt: text("exported_at"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

// Learning Materials for AI Training
export const learningMaterials = pgTable("learning_materials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(), // user-provided title for the material
  grade: integer("grade").notNull(), // 4, 5, 6
  subject: text("subject").notNull(), // mathematics, science, english
  topic: text("topic").notNull(), // specific topic within subject
  filename: text("filename").notNull(), // original filename of uploaded PDF
  pdfPath: text("pdf_path").notNull(), // path in object storage
  extractedText: text("extracted_text"), // full text extracted from PDF
  processingStatus: text("processing_status").notNull().default("pending"), // pending, processing, ready, failed
  errorMessage: text("error_message"), // error details if processing failed
  uploadedBy: varchar("uploaded_by").references(() => users.id), // admin who uploaded
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Relations
export const usersRelations = relations(users, ({ many, one }) => ({
  progress: many(userProgress),
  attempts: many(questionAttempts),
  achievements: many(userAchievements),
  subjectStats: many(userSubjectStats),
  topicStreaks: many(userTopicStreaks),
  level: one(userLevels),
  leaderboardSnapshots: many(leaderboardSnapshots),
  createdQuestions: many(questions),
  uploadedMaterials: many(learningMaterials),
  passwordResetTokens: many(passwordResetTokens),
  sentMessages: many(messages, { relationName: "sentMessages" }),
  receivedMessages: many(messages, { relationName: "receivedMessages" }),
}));

export const userProgressRelations = relations(userProgress, ({ one }) => ({
  user: one(users, {
    fields: [userProgress.userId],
    references: [users.id],
  }),
}));

export const questionAttemptsRelations = relations(questionAttempts, ({ one }) => ({
  user: one(users, {
    fields: [questionAttempts.userId],
    references: [users.id],
  }),
}));

export const userAchievementsRelations = relations(userAchievements, ({ one }) => ({
  user: one(users, {
    fields: [userAchievements.userId],
    references: [users.id],
  }),
}));

export const userSubjectStatsRelations = relations(userSubjectStats, ({ one }) => ({
  user: one(users, {
    fields: [userSubjectStats.userId],
    references: [users.id],
  }),
}));

export const userTopicStreaksRelations = relations(userTopicStreaks, ({ one }) => ({
  user: one(users, {
    fields: [userTopicStreaks.userId],
    references: [users.id],
  }),
}));

export const userLevelsRelations = relations(userLevels, ({ one }) => ({
  user: one(users, {
    fields: [userLevels.userId],
    references: [users.id],
  }),
}));

export const leaderboardSnapshotsRelations = relations(leaderboardSnapshots, ({ one }) => ({
  user: one(users, {
    fields: [leaderboardSnapshots.userId],
    references: [users.id],
  }),
}));

export const questionsRelations = relations(questions, ({ one }) => ({
  createdBy: one(users, {
    fields: [questions.createdBy],
    references: [users.id],
  }),
  stats: one(questionStats, {
    fields: [questions.id],
    references: [questionStats.questionId],
  }),
}));

export const questionStatsRelations = relations(questionStats, ({ one }) => ({
  question: one(questions, {
    fields: [questionStats.questionId],
    references: [questions.id],
  }),
}));

export const learningMaterialsRelations = relations(learningMaterials, ({ one }) => ({
  uploadedBy: one(users, {
    fields: [learningMaterials.uploadedBy],
    references: [users.id],
  }),
}));

export const conversationsRelations = relations(conversations, ({ one, many }) => ({
  user1: one(users, {
    fields: [conversations.user1Id],
    references: [users.id],
  }),
  user2: one(users, {
    fields: [conversations.user2Id],
    references: [users.id],
  }),
  messages: many(messages),
}));

export const messagesRelations = relations(messages, ({ one }) => ({
  conversation: one(conversations, {
    fields: [messages.conversationId],
    references: [conversations.id],
  }),
  sender: one(users, {
    fields: [messages.senderId],
    references: [users.id],
    relationName: "sentMessages",
  }),
  recipient: one(users, {
    fields: [messages.recipientId],
    references: [users.id],
    relationName: "receivedMessages",
  }),
}));

export const passwordResetTokensRelations = relations(passwordResetTokens, ({ one }) => ({
  user: one(users, {
    fields: [passwordResetTokens.userId],
    references: [users.id],
  }),
}));

// Schemas
export const insertUserSchema = createInsertSchema(users).pick({
  password: true,
  firstName: true,
  lastName: true,
  email: true,
  grade: true,
  school: true,
  parentPhone: true,
  signupSource: true,
  signupUtmSource: true,
  signupUtmMedium: true,
  signupUtmCampaign: true,
  signupUtmContent: true,
  signupUtmTerm: true,
  signupFbclid: true,
  signupLandingPath: true,
}).extend({
  email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  signupSource: z.string().optional(),
  signupUtmSource: z.string().optional(),
  signupUtmMedium: z.string().optional(),
  signupUtmCampaign: z.string().optional(),
  signupUtmContent: z.string().optional(),
  signupUtmTerm: z.string().optional(),
  signupFbclid: z.string().optional(),
  signupLandingPath: z.string().optional(),
});

export const insertUserProgressSchema = createInsertSchema(userProgress).omit({
  id: true,
  lastStudiedAt: true,
}).extend({
  completedAt: z.string().datetime().optional().transform((val) => val ? new Date(val) : undefined)
});

export const insertQuestionAttemptSchema = createInsertSchema(questionAttempts).omit({
  id: true,
  attemptedAt: true,
  isReview: true, // server-derived from question grade vs user grade; never client-set
});

export const insertUserAchievementSchema = createInsertSchema(userAchievements).omit({
  id: true,
  earnedAt: true,
});

export const insertUserSubjectStatsSchema = createInsertSchema(userSubjectStats).omit({
  id: true,
  updatedAt: true,
});

export const insertUserTopicStreaksSchema = createInsertSchema(userTopicStreaks).omit({
  id: true,
  lastAttemptAt: true,
});

export const insertUserLevelsSchema = createInsertSchema(userLevels).omit({
  id: true,
  updatedAt: true,
});

export const insertQuestionSchema = createInsertSchema(questions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateQuestionSchema = createInsertSchema(questions).omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export const insertLearningMaterialSchema = createInsertSchema(learningMaterials).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertLeaderboardSnapshotSchema = createInsertSchema(leaderboardSnapshots).omit({
  id: true,
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
});

export const insertQuestionStatsSchema = createInsertSchema(questionStats).omit({
  id: true,
  updatedAt: true,
});

export const updateQuestionStatsSchema = createInsertSchema(questionStats).omit({
  id: true,
  questionId: true,
  updatedAt: true,
}).partial();

export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  createdAt: true,
  lastMessageAt: true,
});

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});

// Types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type UserProgress = typeof userProgress.$inferSelect;
export type InsertUserProgress = z.infer<typeof insertUserProgressSchema>;
export type QuestionAttempt = typeof questionAttempts.$inferSelect;
export type InsertQuestionAttempt = z.infer<typeof insertQuestionAttemptSchema>;
export type UserAchievement = typeof userAchievements.$inferSelect;
export type InsertUserAchievement = z.infer<typeof insertUserAchievementSchema>;
export type UserSubjectStats = typeof userSubjectStats.$inferSelect;
export type InsertUserSubjectStats = z.infer<typeof insertUserSubjectStatsSchema>;
export type UserTopicStreaks = typeof userTopicStreaks.$inferSelect;
export type InsertUserTopicStreaks = z.infer<typeof insertUserTopicStreaksSchema>;
export type UserLevels = typeof userLevels.$inferSelect;
export type InsertUserLevels = z.infer<typeof insertUserLevelsSchema>;
export type LeaderboardSnapshot = typeof leaderboardSnapshots.$inferSelect;
export type InsertLeaderboardSnapshot = z.infer<typeof insertLeaderboardSnapshotSchema>;
export type Question = typeof questions.$inferSelect;
export type InsertQuestion = z.infer<typeof insertQuestionSchema>;
export type UpdateQuestion = z.infer<typeof updateQuestionSchema>;
export type QuestionStats = typeof questionStats.$inferSelect;
export type InsertQuestionStats = z.infer<typeof insertQuestionStatsSchema>;
export type UpdateQuestionStats = z.infer<typeof updateQuestionStatsSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type Conversation = typeof conversations.$inferSelect;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Message = typeof messages.$inferSelect;
export type InsertMessage = z.infer<typeof insertMessageSchema>;

// Prize Rewards
export const prizeRewards = pgTable("prize_rewards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  milestoneType: text("milestone_type").notNull(), // tests_completed, points_earned, streak_achieved
  milestoneValue: integer("milestone_value").notNull(), // e.g., 10 for "10 tests completed"
  prizeAmount: integer("prize_amount").notNull(), // Amount in JMD cents (e.g., 50000 = JMD $500)
  merchantId: text("merchant_id").notNull(), // Giftme merchant ID
  merchantName: text("merchant_name").notNull(), // e.g., "KFC", "Burger King"
  giftmeOrderId: text("giftme_order_id").notNull(), // Unique order ID for Giftme
  giftmeCardCode: text("giftme_card_code"), // Gift card code from Giftme
  giftmeCardPin: text("giftme_card_pin"), // Gift card PIN from Giftme
  status: text("status").notNull().default("pending"), // pending, pending_claim, issued, failed, voided
  deliveredToPhone: text("delivered_to_phone"), // Phone number the card was delivered to
  issuedAt: timestamp("issued_at"),
  errorMessage: text("error_message"), // If failed
  awardedAt: timestamp("awarded_at").defaultNow().notNull(),
});

export const insertPrizeRewardSchema = createInsertSchema(prizeRewards).omit({
  id: true,
  awardedAt: true,
});

export type PrizeReward = typeof prizeRewards.$inferSelect;
export type InsertPrizeReward = z.infer<typeof insertPrizeRewardSchema>;

// Giftme Prize Configuration
export const giftmeConfig = pgTable("giftme_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  milestone: integer("milestone").notNull().unique(), // Min tests required
  prizeAmount: integer("prize_amount").notNull(), // Amount in JMD cents
  enabled: boolean("enabled").notNull().default(true),
  defaultMerchantId: text("default_merchant_id"), // Optional default merchant for this milestone
  defaultMerchantName: text("default_merchant_name"), // Merchant display name
  winProbability: integer("win_probability").notNull().default(100), // % chance of winning per qualifying test (1-100)
  minAvgScore: integer("min_avg_score"), // Optional minimum overall accuracy % required
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertGiftmeConfigSchema = createInsertSchema(giftmeConfig).omit({
  id: true,
  updatedAt: true,
});

export type GiftmeConfig = typeof giftmeConfig.$inferSelect;
export type InsertGiftmeConfig = z.infer<typeof insertGiftmeConfigSchema>;

// Prize Campaigns - Flexible random draw system
export const prizeCampaigns = pgTable("prize_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"), // draft, active, paused, completed
  prizeAmount: integer("prize_amount").notNull(), // Amount in JMD cents
  defaultMerchantId: text("default_merchant_id"), // Default Giftme merchant for winners
  defaultMerchantName: text("default_merchant_name"), // Merchant display name
  winnerCount: integer("winner_count").notNull().default(1), // How many winners to select per draw
  drawFrequency: text("draw_frequency").notNull(), // manual, daily, weekly, monthly
  lastDrawAt: timestamp("last_draw_at"),
  nextDrawAt: timestamp("next_draw_at"),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Campaign eligibility criteria (flexible JSONB-based)
export const prizeCampaignCriteria = pgTable("prize_campaign_criteria", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => prizeCampaigns.id, { onDelete: "cascade" }),
  variable: text("variable").notNull(), // gender, age, school, testsCompleted, subjects, usageFrequency, badgesEarned
  operator: text("operator").notNull(), // equals, not_equals, greater_than, less_than, in, not_in, between, contains
  value: jsonb("value").notNull(), // flexible value storage (string, number, array, object)
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Campaign draw results
export const prizeCampaignResults = pgTable("prize_campaign_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => prizeCampaigns.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  drawId: text("draw_id").notNull(), // groups winners from same draw
  prizeAmount: integer("prize_amount").notNull(),
  merchantId: text("merchant_id"), // Selected merchant
  merchantName: text("merchant_name"),
  giftmeOrderId: text("giftme_order_id"),
  giftmeCardCode: text("giftme_card_code"),
  giftmeCardPin: text("giftme_card_pin"),
  status: text("status").notNull().default("pending"), // staged, pending, issued, failed, claimed, expired (claim window lapsed)
  issuedAt: timestamp("issued_at"),
  claimedAt: timestamp("claimed_at"),
  errorMessage: text("error_message"),
  drawnAt: timestamp("drawn_at").defaultNow().notNull(),
});

export const insertPrizeCampaignSchema = createInsertSchema(prizeCampaigns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPrizeCampaignCriteriaSchema = createInsertSchema(prizeCampaignCriteria).omit({
  id: true,
  createdAt: true,
});

export const insertPrizeCampaignResultSchema = createInsertSchema(prizeCampaignResults).omit({
  id: true,
  drawnAt: true,
});

export type PrizeCampaign = typeof prizeCampaigns.$inferSelect;
export type InsertPrizeCampaign = z.infer<typeof insertPrizeCampaignSchema>;
export type PrizeCampaignCriteria = typeof prizeCampaignCriteria.$inferSelect;
export type InsertPrizeCampaignCriteria = z.infer<typeof insertPrizeCampaignCriteriaSchema>;
export type PrizeCampaignResult = typeof prizeCampaignResults.$inferSelect;
export type InsertPrizeCampaignResult = z.infer<typeof insertPrizeCampaignResultSchema>;

// Prize Notifications - Bot-authored messages for prize claims
export const prizeNotifications = pgTable("prize_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // milestone or campaign
  prizeAmount: integer("prize_amount").notNull(), // Amount in JMD cents
  milestone: integer("milestone"), // nullable, for milestone prizes (10, 25, 50, 100 tests)
  campaignResultId: varchar("campaign_result_id").references(() => prizeCampaignResults.id, { onDelete: "cascade" }), // nullable, for campaign prizes
  dismissedAt: timestamp("dismissed_at"), // null if not dismissed, timestamp if dismissed
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPrizeNotificationSchema = createInsertSchema(prizeNotifications).omit({
  id: true,
  createdAt: true,
});

export type PrizeNotification = typeof prizeNotifications.$inferSelect;
export type InsertPrizeNotification = z.infer<typeof insertPrizeNotificationSchema>;

// Quiz Challenges
export const quizChallenges = pgTable("quiz_challenges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  creatorId: varchar("creator_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  opponentId: varchar("opponent_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  mode: text("mode").notNull(), // "live" or "async"
  status: text("status").notNull().default("pending"), // pending, accepted, in_progress, completed, declined, cancelled, expired
  subject: text("subject").notNull(),
  topic: text("topic"), // optional, can be null for mixed topics
  grade: integer("grade").notNull(),
  questionCount: integer("question_count").notNull().default(10),
  questionIds: text("question_ids").array().notNull(), // fixed order of question IDs
  timeLimit: integer("time_limit"), // optional time limit in seconds
  createdAt: timestamp("created_at").defaultNow().notNull(),
  acceptedAt: timestamp("accepted_at"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  expiresAt: timestamp("expires_at"), // for pending invitations
});

export const quizChallengeAttempts = pgTable("quiz_challenge_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  challengeId: varchar("challenge_id").notNull().references(() => quizChallenges.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  answers: jsonb("answers").notNull(), // array of { questionId, selectedAnswer, isCorrect, pointsEarned }
  totalScore: integer("total_score").notNull().default(0),
  correctCount: integer("correct_count").notNull().default(0),
  timeSpent: integer("time_spent"), // total time in seconds
  currentQuestionIndex: integer("current_question_index").notNull().default(0), // for live progress tracking
  lastSyncedAt: timestamp("last_synced_at"), // for reconnection validation
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Discount Codes
export const discountCodes = pgTable("discount_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(), // e.g., SUMMER30, TRIAL14
  type: text("type").notNull(), // "percentage" or "trial"
  value: integer("value").notNull(), // 30 for 30% off, or 14 for 14-day trial
  applicablePlans: text("applicable_plans").array(), // ["monthly", "yearly"] or null for all
  maxUses: integer("max_uses"), // null for unlimited
  timesUsed: integer("times_used").notNull().default(0),
  stripeCouponId: text("stripe_coupon_id"), // Stripe coupon ID for percentage discounts
  active: boolean("active").notNull().default(true),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: varchar("created_by").references(() => users.id), // admin who created it
  linkSlug: text("link_slug").unique(), // optional slug for shareable trial URL e.g. /trial/back-to-school
});

// Discount Code Usage Tracking
export const discountCodeUsage = pgTable("discount_code_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  discountCodeId: varchar("discount_code_id").notNull().references(() => discountCodes.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  subscriptionId: text("subscription_id"), // Stripe subscription ID
  discountAmount: integer("discount_amount"), // amount saved in cents
  usedAt: timestamp("used_at").defaultNow().notNull(),
});

// Referral Partners (Separate auth system from students)
export const referralPartners = pgTable("referral_partners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  phoneNumber: text("phone_number"),
  referralCode: text("referral_code").notNull().unique(),
  programType: text("program_type").notNull().default("referral"),
  socialLink: text("social_link"),
  minPayoutThreshold: integer("min_payout_threshold").notNull().default(2000),
  bankAccountName: text("bank_account_name"),
  bankAccountNumber: text("bank_account_number"),
  bankName: text("bank_name"),
  totalEarnings: integer("total_earnings").notNull().default(0),
  pendingPayout: integer("pending_payout").notNull().default(0),
  lifetimePayouts: integer("lifetime_payouts").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastLoginAt: timestamp("last_login_at"),
});

// Referrals - Links partners to referred users
export const referrals = pgTable("referrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull().references(() => referralPartners.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }), // unique constraint ensures each user can only be referred by one partner
  isActive: boolean("is_active").notNull().default(true), // true if user has active subscription
  firstSubscribedAt: timestamp("first_subscribed_at"),
  lastActiveSubscriptionDate: timestamp("last_active_subscription_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Referral Payouts
export const referralPayouts = pgTable("referral_payouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull().references(() => referralPartners.id, { onDelete: "cascade" }),
  amount: integer("amount").notNull(), // in JMD cents
  monthYear: text("month_year").notNull(), // "2025-03" format
  activeReferralsCount: integer("active_referrals_count").notNull(),
  payoutMethod: text("payout_method").notNull().default("bank_transfer"),
  status: text("status").notNull().default("pending"), // pending, completed, failed
  processedBy: varchar("processed_by").references(() => users.id), // admin who processed it
  processedAt: timestamp("processed_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Content Creator Applications
export const contentCreatorApplications = pgTable("content_creator_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull(),
  phoneNumber: text("phone_number"),
  socialLink: text("social_link"),
  instagramHandle: text("instagram_handle"),
  tiktokHandle: text("tiktok_handle"),
  youtubeHandle: text("youtube_handle"),
  estimatedAudience: text("estimated_audience"),
  referralCode: text("referral_code"),
  status: text("status").notNull().default("pending"),
  adminNotes: text("admin_notes"),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  setupToken: text("setup_token").unique(),
  setupTokenExpiresAt: timestamp("setup_token_expires_at"),
  setupTokenUsed: boolean("setup_token_used").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Creator Videos - for tracking content creator video submissions
export const creatorVideos = pgTable("creator_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull().references(() => referralPartners.id, { onDelete: "cascade" }),
  videoUrl: text("video_url").notNull(),
  platform: text("platform").notNull(), // "tiktok" or "instagram"
  currentViews: integer("current_views").notNull().default(0),
  milestonesHit: integer("milestones_hit").notNull().default(0), // number of 10k milestones reached
  milestonesEarned: integer("milestones_earned").notNull().default(0), // total JMD earned from view milestones
  conversions: integer("conversions").notNull().default(0),
  status: text("status").notNull().default("active"), // "active" or "expired"
  adminNotes: text("admin_notes"),
  lastViewUpdate: timestamp("last_view_update"),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at").notNull(), // 90 days from submission
});

// Notifications
export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // challenge_received, challenge_accepted, prize_won, etc
  title: text("title").notNull(),
  message: text("message").notNull(),
  relatedId: varchar("related_id"), // ID of related entity (challenge, prize, etc)
  relatedUrl: text("related_url"), // URL to navigate to when clicked
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertQuizChallengeSchema = createInsertSchema(quizChallenges).omit({
  id: true,
  createdAt: true,
});

export const insertQuizChallengeAttemptSchema = createInsertSchema(quizChallengeAttempts).omit({
  id: true,
  createdAt: true,
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
});

// QA Test Runs - for automated daily testing
export const qaTestRuns = pgTable("qa_test_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  testType: text("test_type").notNull(), // 'signup_payment' or 'quiz_taking'
  status: text("status").notNull(), // 'pass', 'fail', 'running'
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  durationMs: integer("duration_ms"), // duration in milliseconds
  errorMessage: text("error_message"),
  errorStack: text("error_stack"),
  metadata: text("metadata"), // JSON string with additional details
}, (table) => ({
  testTypeIdx: index("qa_test_runs_test_type_idx").on(table.testType, table.startedAt),
  statusIdx: index("qa_test_runs_status_idx").on(table.status),
}));

export const insertQaTestRunSchema = createInsertSchema(qaTestRuns).omit({
  id: true,
  startedAt: true,
});

// Brevo Field Mappings - configurable field mapping for Brevo contacts
export const brevoFieldMappings = pgTable("brevo_field_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userField: text("user_field").notNull().unique(), // firstName, lastName, school, etc
  brevoAttribute: text("brevo_attribute").notNull(), // FIRSTNAME, LASTNAME, SCHOOL, etc
  isActive: boolean("is_active").notNull().default(true), // enable/disable mapping
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertBrevoFieldMappingSchema = createInsertSchema(brevoFieldMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Curriculum Management - Grades/Levels
export const grades = pgTable("grades", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gradeNumber: integer("grade_number").notNull().unique(), // 4, 5, 6, etc
  displayName: text("display_name").notNull(), // "Grade 4", "Form 1", etc
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0), // for custom ordering
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Curriculum Management - Subjects
export const subjects = pgTable("subjects", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gradeId: varchar("grade_id").notNull().references(() => grades.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "Math", "Science", "English", etc
  displayName: text("display_name").notNull(), // "Mathematics", "English Language Arts", etc
  icon: text("icon"), // lucide icon name or emoji
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0), // for custom ordering
  trainingText: text("training_text"),
  trainingFiles: jsonb("training_files").$type<Array<{filename: string, mimeType: string, base64: string, uploadedAt: string}>>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Curriculum Management - Topics
export const topics = pgTable("topics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subjectId: varchar("subject_id").notNull().references(() => subjects.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "Number", "Geometry", etc
  displayName: text("display_name").notNull(), // "Number Operations", "Geometric Shapes", etc
  icon: text("icon"), // lucide icon name or emoji
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0), // for custom ordering
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Curated cross-grade review links: for a topic a student studies, the foundational topic in the
// grade DIRECTLY below to revise in Review mode. Distinct from topic_prerequisites (within-grade,
// learned analytics); this map is curriculum-curated plus auto-seeded same-name matches, and drives
// the "Revise the basics" suggestion. Keyed by canonical subject + normalized topicKey
// (shared/curriculum.ts). prereqGrade is always grade-1, matching review mode's grade-below rule.
export const topicReviewLinks = pgTable("topic_review_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subject: text("subject").notNull(),
  grade: integer("grade").notNull(),
  topicKey: text("topic_key").notNull(),
  topicDisplay: text("topic_display").notNull(),
  prereqGrade: integer("prereq_grade").notNull(),
  prereqTopicKey: text("prereq_topic_key").notNull(),
  prereqTopicDisplay: text("prereq_topic_display").notNull(),
  source: text("source").notNull().default("curated"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  linkUnique: uniqueIndex("topic_review_links_unique").on(table.subject, table.grade, table.topicKey, table.prereqTopicKey),
}));

export const insertGradeSchema = createInsertSchema(grades).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSubjectSchema = createInsertSchema(subjects).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTopicSchema = createInsertSchema(topics).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDiscountCodeSchema = createInsertSchema(discountCodes).omit({
  id: true,
  createdAt: true,
  timesUsed: true,
});

export const insertDiscountCodeUsageSchema = createInsertSchema(discountCodeUsage).omit({
  id: true,
  usedAt: true,
});

export const insertCreatorVideoSchema = createInsertSchema(creatorVideos).omit({
  id: true,
  currentViews: true,
  milestonesHit: true,
  milestonesEarned: true,
  conversions: true,
  status: true,
  adminNotes: true,
  lastViewUpdate: true,
  submittedAt: true,
});

export const insertContentCreatorApplicationSchema = createInsertSchema(contentCreatorApplications).omit({
  id: true,
  createdAt: true,
  status: true,
  adminNotes: true,
  reviewedBy: true,
  reviewedAt: true,
  setupToken: true,
  setupTokenExpiresAt: true,
  setupTokenUsed: true,
});

export const insertReferralPartnerSchema = createInsertSchema(referralPartners).omit({
  id: true,
  createdAt: true,
  totalEarnings: true,
  pendingPayout: true,
  lifetimePayouts: true,
  lastLoginAt: true,
});

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true,
  createdAt: true,
});

export const insertReferralPayoutSchema = createInsertSchema(referralPayouts).omit({
  id: true,
  createdAt: true,
});

export type QuizChallenge = typeof quizChallenges.$inferSelect;
export type InsertQuizChallenge = z.infer<typeof insertQuizChallengeSchema>;
export type QuizChallengeAttempt = typeof quizChallengeAttempts.$inferSelect;
export type InsertQuizChallengeAttempt = z.infer<typeof insertQuizChallengeAttemptSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type DiscountCode = typeof discountCodes.$inferSelect;
export type InsertDiscountCode = z.infer<typeof insertDiscountCodeSchema>;
export type DiscountCodeUsage = typeof discountCodeUsage.$inferSelect;
export type InsertDiscountCodeUsage = z.infer<typeof insertDiscountCodeUsageSchema>;
export type CreatorVideo = typeof creatorVideos.$inferSelect;
export type InsertCreatorVideo = z.infer<typeof insertCreatorVideoSchema>;
export type ContentCreatorApplication = typeof contentCreatorApplications.$inferSelect;
export type InsertContentCreatorApplication = z.infer<typeof insertContentCreatorApplicationSchema>;
export type ReferralPartner = typeof referralPartners.$inferSelect;
export type InsertReferralPartner = z.infer<typeof insertReferralPartnerSchema>;
export type Referral = typeof referrals.$inferSelect;
export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type ReferralPayout = typeof referralPayouts.$inferSelect;
export type InsertReferralPayout = z.infer<typeof insertReferralPayoutSchema>;
export type QaTestRun = typeof qaTestRuns.$inferSelect;
export type InsertQaTestRun = z.infer<typeof insertQaTestRunSchema>;
export type BrevoFieldMapping = typeof brevoFieldMappings.$inferSelect;
export type InsertBrevoFieldMapping = z.infer<typeof insertBrevoFieldMappingSchema>;
export type LearningMaterial = typeof learningMaterials.$inferSelect;
export type InsertLearningMaterial = z.infer<typeof insertLearningMaterialSchema>;
export type Grade = typeof grades.$inferSelect;
export type InsertGrade = z.infer<typeof insertGradeSchema>;
export type Subject = typeof subjects.$inferSelect;
export type InsertSubject = z.infer<typeof insertSubjectSchema>;
export type Topic = typeof topics.$inferSelect;
export type InsertTopic = z.infer<typeof insertTopicSchema>;

// Mobile API Refresh Tokens
export const refreshTokens = pgTable("refresh_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  token: text("token").notNull().unique(),
  deviceInfo: text("device_info"), // iOS, Android, etc.
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at").defaultNow().notNull(),
  isRevoked: boolean("is_revoked").default(false).notNull(),
}, (table) => ({
  userIdIdx: index("refresh_tokens_user_id_idx").on(table.userId),
  tokenIdx: index("refresh_tokens_token_idx").on(table.token),
}));

export const insertRefreshTokenSchema = createInsertSchema(refreshTokens).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
});

export type RefreshToken = typeof refreshTokens.$inferSelect;
export type InsertRefreshToken = z.infer<typeof insertRefreshTokenSchema>;

// Activity Events for Admin Stream
export const activityEvents = pgTable("activity_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  actorId: varchar("actor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(), // message.sent, question.attempted, quiz.started, quiz.completed, login, etc.
  targetId: varchar("target_id"), // Optional: recipient user ID for messages, etc.
  subjectType: text("subject_type"), // message, question, quiz, etc.
  subjectId: varchar("subject_id"), // ID of the related entity
  metadata: jsonb("metadata").default("{}"), // Additional context (names, scores, subject, topic, etc.)
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  actorIdIdx: index("activity_events_actor_id_idx").on(table.actorId),
  eventTypeIdx: index("activity_events_event_type_idx").on(table.eventType),
  createdAtIdx: index("activity_events_created_at_idx").on(table.createdAt),
}));

export const insertActivityEventSchema = createInsertSchema(activityEvents).omit({
  id: true,
  createdAt: true,
});

export type ActivityEvent = typeof activityEvents.$inferSelect;
export type InsertActivityEvent = z.infer<typeof insertActivityEventSchema>;

// Reading Materials - Content for students to read
export const readingMaterials = pgTable("reading_materials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  grade: integer("grade").notNull(), // 4, 5, 6
  subject: text("subject").notNull(), // matches subjects.name
  topic: text("topic").notNull(), // matches topics.name
  content: text("content").notNull(), // rich text HTML content
  summary: text("summary"), // brief description
  coverImageUrl: text("cover_image_url"), // hero/cover image
  estimatedReadTime: integer("estimated_read_time").notNull().default(5), // in minutes
  status: text("status").notNull().default("draft"), // draft, published, archived
  createdBy: varchar("created_by").references(() => users.id),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  gradeIdx: index("reading_materials_grade_idx").on(table.grade),
  subjectIdx: index("reading_materials_subject_idx").on(table.subject),
  topicIdx: index("reading_materials_topic_idx").on(table.topic),
  statusIdx: index("reading_materials_status_idx").on(table.status),
}));

// Reading Progress - Track student reading activity
export const readingProgress = pgTable("reading_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  materialId: varchar("material_id").notNull().references(() => readingMaterials.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("not_started"), // not_started, in_progress, completed
  scrollPosition: real("scroll_position").default(0), // percentage 0-100
  totalTimeSeconds: integer("total_time_seconds").notNull().default(0), // total time spent reading
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  lastReadAt: timestamp("last_read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("reading_progress_user_id_idx").on(table.userId),
  materialIdIdx: index("reading_progress_material_id_idx").on(table.materialId),
  userMaterialIdx: index("reading_progress_user_material_idx").on(table.userId, table.materialId),
}));

// Reading Sessions - Track individual reading sessions for detailed analytics
export const readingSessions = pgTable("reading_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  materialId: varchar("material_id").notNull().references(() => readingMaterials.id, { onDelete: "cascade" }),
  progressId: varchar("progress_id").notNull().references(() => readingProgress.id, { onDelete: "cascade" }),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  durationSeconds: integer("duration_seconds").default(0),
  startPosition: real("start_position").default(0), // scroll position at start
  endPosition: real("end_position").default(0), // scroll position at end
});

export const readingMaterialsRelations = relations(readingMaterials, ({ one, many }) => ({
  createdBy: one(users, {
    fields: [readingMaterials.createdBy],
    references: [users.id],
  }),
  progress: many(readingProgress),
}));

export const readingProgressRelations = relations(readingProgress, ({ one, many }) => ({
  user: one(users, {
    fields: [readingProgress.userId],
    references: [users.id],
  }),
  material: one(readingMaterials, {
    fields: [readingProgress.materialId],
    references: [readingMaterials.id],
  }),
  sessions: many(readingSessions),
}));

export const readingSessionsRelations = relations(readingSessions, ({ one }) => ({
  user: one(users, {
    fields: [readingSessions.userId],
    references: [users.id],
  }),
  material: one(readingMaterials, {
    fields: [readingSessions.materialId],
    references: [readingMaterials.id],
  }),
  progress: one(readingProgress, {
    fields: [readingSessions.progressId],
    references: [readingProgress.id],
  }),
}));

export const insertReadingMaterialSchema = createInsertSchema(readingMaterials).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateReadingMaterialSchema = createInsertSchema(readingMaterials).omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export const insertReadingProgressSchema = createInsertSchema(readingProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertReadingSessionSchema = createInsertSchema(readingSessions).omit({
  id: true,
});

export type ReadingMaterial = typeof readingMaterials.$inferSelect;
export type InsertReadingMaterial = z.infer<typeof insertReadingMaterialSchema>;
export type UpdateReadingMaterial = z.infer<typeof updateReadingMaterialSchema>;
export type ReadingProgress = typeof readingProgress.$inferSelect;
export type InsertReadingProgress = z.infer<typeof insertReadingProgressSchema>;
export type ReadingSession = typeof readingSessions.$inferSelect;
export type InsertReadingSession = z.infer<typeof insertReadingSessionSchema>;

export const unsubscribeFeedback = pgTable("unsubscribe_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  reason: text("reason").notNull(),
  otherDetails: text("other_details"),
  subscriptionStatus: text("subscription_status"),
  subscriptionPlan: text("subscription_plan"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  mrrLost: integer("mrr_lost").notNull().default(0),
  offerShown: boolean("offer_shown").notNull().default(false),
  offerAccepted: boolean("offer_accepted").notNull().default(false),
  monthsSubscribed: integer("months_subscribed"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertUnsubscribeFeedbackSchema = createInsertSchema(unsubscribeFeedback).omit({ id: true, createdAt: true });
export type InsertUnsubscribeFeedback = z.infer<typeof insertUnsubscribeFeedbackSchema>;
export type UnsubscribeFeedback = typeof unsubscribeFeedback.$inferSelect;

export const insertQuestionObjectiveSchema = createInsertSchema(questionObjectives).omit({ id: true, createdAt: true });
export type QuestionObjective = typeof questionObjectives.$inferSelect;
export type InsertQuestionObjective = z.infer<typeof insertQuestionObjectiveSchema>;

export type QuizzativeStandard = typeof quizzativeStandards.$inferSelect;
export type QuizzativeTopic = typeof quizzativeTopics.$inferSelect;
export type QuizzativeObjective = typeof quizzativeObjectives.$inferSelect;

export const insertQuizzativeSyncLogSchema = createInsertSchema(quizzativeSyncLog).omit({ id: true, startedAt: true });
export type QuizzativeSyncLog = typeof quizzativeSyncLog.$inferSelect;
export type InsertQuizzativeSyncLog = z.infer<typeof insertQuizzativeSyncLogSchema>;

// Academic Calendar Engine
export const academicYears = pgTable("academic_years", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  label: text("label").notNull(), // "2025-2026"
  startDate: timestamp("start_date").notNull(), // Sept 1
  endDate: timestamp("end_date").notNull(), // Aug 31
  contactDays: integer("contact_days").notNull().default(190),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const academicTerms = pgTable("academic_terms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  academicYearId: varchar("academic_year_id").notNull().references(() => academicYears.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "Christmas Term", "Easter Term", "Summer Term"
  shortName: text("short_name").notNull(), // "christmas", "easter", "summer"
  termNumber: integer("term_number").notNull(), // 1, 2, 3
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const termBreaks = pgTable("term_breaks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  academicYearId: varchar("academic_year_id").notNull().references(() => academicYears.id, { onDelete: "cascade" }),
  termId: varchar("term_id").references(() => academicTerms.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "National Heroes Day Break", "Christmas Break", "Easter Break"
  type: text("type").notNull().default("holiday"), // "mid-term", "holiday", "vacation"
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const pepWindows = pgTable("pep_windows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  academicYearId: varchar("academic_year_id").notNull().references(() => academicYears.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "Ability Test", "Performance Task", "Grade Six Achievement Test"
  windowType: text("window_type").notNull(), // "ability_test", "performance_task", "gsat"
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  applicableGrades: integer("applicable_grades").array().notNull(), // [4, 5, 6]
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const academicYearsRelations = relations(academicYears, ({ many }) => ({
  terms: many(academicTerms),
  breaks: many(termBreaks),
  pepWindows: many(pepWindows),
}));

export const academicTermsRelations = relations(academicTerms, ({ one, many }) => ({
  academicYear: one(academicYears, {
    fields: [academicTerms.academicYearId],
    references: [academicYears.id],
  }),
  breaks: many(termBreaks),
}));

export const termBreaksRelations = relations(termBreaks, ({ one }) => ({
  academicYear: one(academicYears, {
    fields: [termBreaks.academicYearId],
    references: [academicYears.id],
  }),
  term: one(academicTerms, {
    fields: [termBreaks.termId],
    references: [academicTerms.id],
  }),
}));

export const pepWindowsRelations = relations(pepWindows, ({ one }) => ({
  academicYear: one(academicYears, {
    fields: [pepWindows.academicYearId],
    references: [academicYears.id],
  }),
}));

export const insertAcademicYearSchema = createInsertSchema(academicYears).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcademicTermSchema = createInsertSchema(academicTerms).omit({ id: true, createdAt: true });
export const insertTermBreakSchema = createInsertSchema(termBreaks).omit({ id: true, createdAt: true });
export const insertPepWindowSchema = createInsertSchema(pepWindows).omit({ id: true, createdAt: true });

export type AcademicYear = typeof academicYears.$inferSelect;
export type InsertAcademicYear = z.infer<typeof insertAcademicYearSchema>;
export type AcademicTerm = typeof academicTerms.$inferSelect;
export type InsertAcademicTerm = z.infer<typeof insertAcademicTermSchema>;
export type TermBreak = typeof termBreaks.$inferSelect;
export type InsertTermBreak = z.infer<typeof insertTermBreakSchema>;
export type PepWindow = typeof pepWindows.$inferSelect;
export type InsertPepWindow = z.infer<typeof insertPepWindowSchema>;

export const parentVerificationCodes = pgTable("parent_verification_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  parentEmail: text("parent_email").notNull(),
  codeHash: text("code_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  attempts: integer("attempts").notNull().default(0),
  verified: boolean("verified").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertParentVerificationCodeSchema = createInsertSchema(parentVerificationCodes).omit({ id: true, createdAt: true });
export type ParentVerificationCode = typeof parentVerificationCodes.$inferSelect;
export type InsertParentVerificationCode = z.infer<typeof insertParentVerificationCodeSchema>;

// ───────────────────────── Parent Dashboard v2 ─────────────────────────
// Parent identity as a first-class entity. Replaces the denormalised
// users.parent_email / parent_phone columns as the source of truth.

export const parents = pgTable("parents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email"),
  phone: text("phone"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  notificationPrefs: jsonb("notification_prefs").$type<{ email: boolean; whatsapp: boolean }>().default({ email: true, whatsapp: true }),
  emailVerifiedAt: timestamp("email_verified_at"),
  phoneVerifiedAt: timestamp("phone_verified_at"),
  lastLoginAt: timestamp("last_login_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  emailLowerIdx: uniqueIndex("parents_email_lower_idx").on(sql`lower(${t.email})`),
  phoneDigitsIdx: uniqueIndex("parents_phone_digits_idx").on(sql`regexp_replace(${t.phone}, '\\D', '', 'g')`),
}));

export const parentChildren = pgTable("parent_children", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  parentId: varchar("parent_id").notNull().references(() => parents.id, { onDelete: "cascade" }),
  childUserId: varchar("child_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  relationship: text("relationship").notNull().default("guardian"), // mother | father | guardian | other
  isPrimary: boolean("is_primary").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  parentChildUnique: unique().on(t.parentId, t.childUserId),
  childIdx: index("parent_children_child_idx").on(t.childUserId),
}));

export const parentSessions = pgTable("parent_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  parentId: varchar("parent_id").notNull().references(() => parents.id, { onDelete: "cascade" }),
  expiresAt: timestamp("expires_at").notNull(),
  ip: text("ip"),
  userAgent: text("user_agent"),
  lastActiveAt: timestamp("last_active_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  parentIdx: index("parent_sessions_parent_idx").on(t.parentId),
  expiresIdx: index("parent_sessions_expires_idx").on(t.expiresAt),
}));

export const parentOtpCodes = pgTable("parent_otp_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  identifier: text("identifier").notNull(),           // normalised email (lower) or phone (E.164)
  identifierType: text("identifier_type").notNull(),  // 'email' | 'phone'
  codeHash: text("code_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  attempts: integer("attempts").notNull().default(0),
  consumedAt: timestamp("consumed_at"),
  ip: text("ip"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  activeIdx: index("parent_otp_active_idx").on(t.identifier, t.expiresAt),
}));

export const parentNotifications = pgTable("parent_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  parentId: varchar("parent_id").notNull().references(() => parents.id, { onDelete: "cascade" }),
  type: text("type").notNull(),       // dunning | weekly_digest | class_reminder | milestone | system
  title: text("title").notNull(),
  body: text("body").notNull(),
  ctaUrl: text("cta_url"),
  meta: jsonb("meta").$type<Record<string, unknown>>(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  parentUnreadIdx: index("parent_notifications_parent_unread_idx").on(t.parentId, t.readAt),
}));

export const insertParentSchema = createInsertSchema(parents).omit({ id: true, createdAt: true, updatedAt: true });
export type Parent = typeof parents.$inferSelect;
export type InsertParent = z.infer<typeof insertParentSchema>;

export const insertParentChildSchema = createInsertSchema(parentChildren).omit({ id: true, createdAt: true });
export type ParentChild = typeof parentChildren.$inferSelect;
export type InsertParentChild = z.infer<typeof insertParentChildSchema>;

export const insertParentSessionSchema = createInsertSchema(parentSessions).omit({ id: true, createdAt: true, lastActiveAt: true });
export type ParentSession = typeof parentSessions.$inferSelect;
export type InsertParentSession = z.infer<typeof insertParentSessionSchema>;

export const insertParentOtpSchema = createInsertSchema(parentOtpCodes).omit({ id: true, createdAt: true });
export type ParentOtpCode = typeof parentOtpCodes.$inferSelect;
export type InsertParentOtpCode = z.infer<typeof insertParentOtpSchema>;

export const insertParentNotificationSchema = createInsertSchema(parentNotifications).omit({ id: true, createdAt: true });
export type ParentNotification = typeof parentNotifications.$inferSelect;
export type InsertParentNotification = z.infer<typeof insertParentNotificationSchema>;

export const whatsappMessages = pgTable("whatsapp_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"),
  recipientPhone: text("recipient_phone").notNull(),
  direction: text("direction").notNull().default("outbound"),
  status: text("status").notNull().default("sent"),
  messageType: text("message_type").notNull().default("text"),
  messageBody: text("message_body"),
  mediaUrl: text("media_url"),
  mediaContentType: text("media_content_type"),
  kapsoMessageId: text("kapso_message_id"),
  conversationId: text("conversation_id"),
  eventType: text("event_type"),
  rawPayload: jsonb("raw_payload"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertWhatsappMessageSchema = createInsertSchema(whatsappMessages).omit({ id: true, createdAt: true });
export type WhatsappMessage = typeof whatsappMessages.$inferSelect;
export type InsertWhatsappMessage = z.infer<typeof insertWhatsappMessageSchema>;

export const whatsappOtpCodes = pgTable("whatsapp_otp_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  phone: text("phone").notNull(),
  codeHash: text("code_hash").notNull(),
  purpose: text("purpose").notNull().default("password_reset"),
  expiresAt: timestamp("expires_at").notNull(),
  attempts: integer("attempts").notNull().default(0),
  verified: boolean("verified").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertWhatsappOtpSchema = createInsertSchema(whatsappOtpCodes).omit({ id: true, createdAt: true });
export type WhatsappOtpCode = typeof whatsappOtpCodes.$inferSelect;
export type InsertWhatsappOtpCode = z.infer<typeof insertWhatsappOtpSchema>;

// WhatsApp Marketing Contacts — tracks opt-in/opt-out consent per phone number
export const whatsappContacts = pgTable("whatsapp_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  phone: text("phone").notNull().unique(), // digits only, no + prefix, e.g. 18768228543
  whatsappMarketingOptIn: boolean("whatsapp_marketing_opt_in").notNull().default(false),
  whatsappMarketingOptInAt: timestamp("whatsapp_marketing_opt_in_at"),
  whatsappMarketingOptOut: boolean("whatsapp_marketing_opt_out").notNull().default(false),
  whatsappMarketingOptOutAt: timestamp("whatsapp_marketing_opt_out_at"),
  lastAdminReadAt: timestamp("last_admin_read_at"),
  humanTookOverAt: timestamp("human_took_over_at"), // set when admin replies; Claude pauses for 2h from this
  parentName: text("parent_name"), // collected upfront before enrollment — persists across enrollments
  enrollmentState: text("enrollment_state"), // null | 'awaiting_parent_name' | 'awaiting_child_name' | 'awaiting_email' | 'awaiting_grade' | 'awaiting_plan' | 'awaiting_payment'
  enrollmentName: text("enrollment_name"),
  enrollmentEmail: text("enrollment_email"),
  enrollmentGrade: integer("enrollment_grade"),
  enrollmentPlan: text("enrollment_plan"),
  whatsappConversationState: text("whatsapp_conversation_state"), // parent help flow: null | 'awaiting_login_details' | 'awaiting_cancel_details'
  // Menu-driven flow state machine
  flowNode: text("flow_node"), // current node in the flow tree (e.g. 'main_menu', 'academy_menu', 'progress_awaiting_email')
  flowContext: jsonb("flow_context").$type<Record<string, any>>(), // transient data collected during a flow (email, child name, etc.)
  flowUpdatedAt: timestamp("flow_updated_at").defaultNow(), // for timeout detection (30 min → reset to main menu)
  contactName: text("contact_name"), // name collected from first-time contacts (separate from parentName which is enrollment-specific)
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertWhatsappContactSchema = createInsertSchema(whatsappContacts).omit({ id: true, createdAt: true, updatedAt: true });
export type WhatsappContact = typeof whatsappContacts.$inferSelect;
export type InsertWhatsappContact = z.infer<typeof insertWhatsappContactSchema>;

export const quizzativeApiLogs = pgTable("quizzative_api_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  endpoint: text("endpoint").notNull(),
  method: text("method").notNull(),
  summary: text("summary").notNull(),
  statusCode: integer("status_code"),
  responseTimeMs: integer("response_time_ms"),
  errorMessage: text("error_message"),
  success: boolean("success").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type QuizzativeApiLog = typeof quizzativeApiLogs.$inferSelect;

export const quizzativeBulkExportRuns = pgTable("quizzative_bulk_export_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  status: text("status").notNull().default("running"),
  mode: text("mode").notNull().default("all"),
  totalQuestions: integer("total_questions").notNull().default(0),
  totalBatches: integer("total_batches").notNull().default(0),
  batchesCompleted: integer("batches_completed").notNull().default(0),
  questionsSent: integer("questions_sent").notNull().default(0),
  questionsIdMapped: integer("questions_id_mapped").notNull().default(0),
  lastQuestionId: text("last_question_id"),
  errors: jsonb("errors").default("[]"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

export type QuizzativeBulkExportRun = typeof quizzativeBulkExportRuns.$inferSelect;

export const dailyParentNotifications = pgTable("daily_parent_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  parentPhone: text("parent_phone").notNull(),
  notificationDate: text("notification_date").notNull(),
  questionsAnswered: integer("questions_answered").notNull(),
  accuracy: integer("accuracy"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
});

export type DailyParentNotification = typeof dailyParentNotifications.$inferSelect;

// Platform Banners
export const platformBanners = pgTable("platform_banners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  message: text("message").notNull(),
  type: text("type").notNull().default("info"),
  isActive: boolean("is_active").default(false),
  linkUrl: text("link_url"),
  linkText: text("link_text"),
  startsAt: timestamp("starts_at"),
  endsAt: timestamp("ends_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPlatformBannerSchema = createInsertSchema(platformBanners).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type PlatformBanner = typeof platformBanners.$inferSelect;
export type InsertPlatformBanner = z.infer<typeof insertPlatformBannerSchema>;

// Question Reports
export const questionReports = pgTable("question_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull(),
  userId: varchar("user_id").notNull(),
  reason: text("reason").notNull(),
  details: text("details"),
  status: text("status").notNull().default("pending"),
  adminNotes: text("admin_notes"),
  proposedFix: jsonb("proposed_fix"),
  aiAnalysis: text("ai_analysis"),
  voidedAttempts: jsonb("voided_attempts").$type<Array<{ id: string; isCorrect: boolean; pointsEarned: number; attemptedAt: string; selectedAnswer: string | null }>>().notNull().default([]),
  source: text("source"),
  createdAt: timestamp("created_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertQuestionReportSchema = createInsertSchema(questionReports).omit({
  id: true,
  createdAt: true,
  resolvedAt: true,
  status: true,
  adminNotes: true,
  proposedFix: true,
  voidedAttempts: true,
  source: true,
  aiAnalysis: true,
});

export type QuestionReport = typeof questionReports.$inferSelect;
export type InsertQuestionReport = z.infer<typeof insertQuestionReportSchema>;

// Weekly WhatsApp Report Batch Logs
export const weeklyReportBatchLogs = pgTable("weekly_report_batch_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  triggeredBy: text("triggered_by").notNull().default("scheduler"), // "scheduler" | "admin"
  totalParents: integer("total_parents").notNull().default(0),
  sent: integer("sent").notNull().default(0),
  failed: integer("failed").notNull().default(0),
  skipped: integer("skipped").notNull().default(0),
  errors: jsonb("errors").default("[]"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});
export type WeeklyReportBatchLog = typeof weeklyReportBatchLogs.$inferSelect;

// API Keys
export const apiKeys = pgTable("api_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  keyHash: text("key_hash").notNull(),
  keyPrefix: text("key_prefix").notNull(),
  createdBy: varchar("created_by").notNull().references(() => users.id, { onDelete: "cascade" }),
  lastUsedAt: timestamp("last_used_at"),
  expiresAt: timestamp("expires_at"),
  isActive: boolean("is_active").notNull().default(true),
  scopes: text("scopes").array().notNull().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  createdByIdx: index("api_keys_created_by_idx").on(table.createdBy),
  keyHashIdx: index("api_keys_key_hash_idx").on(table.keyHash),
}));

export const insertApiKeySchema = createInsertSchema(apiKeys).omit({
  id: true,
  createdAt: true,
  keyHash: true,
  keyPrefix: true,
  lastUsedAt: true,
});

export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;

// Push notification tokens (mobile app)
export const pushTokens = pgTable("push_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  token: text("token").notNull(),
  platform: text("platform").notNull().default("ios"), // ios, android
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userTokenIdx: index("push_tokens_user_id_idx").on(table.userId),
  tokenIdx: uniqueIndex("push_tokens_token_idx").on(table.token),
}));

export type PushToken = typeof pushTokens.$inferSelect;

// Web Push subscriptions (browser/PWA, sent via web-push VAPID)
export const pushSubscriptions = pgTable("push_subscriptions", {
  endpoint: text("endpoint").primaryKey(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("push_subscriptions_user_id_idx").on(table.userId),
}));

export type PushSubscription = typeof pushSubscriptions.$inferSelect;

// Persistent Telegram message_id → WhatsApp phone mapping (survives server restarts)
export const telegramMessageMap = pgTable("telegram_message_map", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  telegramMessageId: integer("telegram_message_id").notNull().unique(),
  whatsappPhone: text("whatsapp_phone").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Knowledge Base Articles (for Nya WhatsApp AI)
export const kbArticles = pgTable('kb_articles', {
  id: serial('id').primaryKey(),
  title: text('title').notNull(),
  category: text('category').notNull(),
  content: text('content').notNull(),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

export type KbArticle = typeof kbArticles.$inferSelect;
export type InsertKbArticle = typeof kbArticles.$inferInsert;

// WhatsApp conversation tags
export const whatsappTags = pgTable("whatsapp_tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  color: text("color").notNull().default("slate"), // slate|red|orange|amber|green|teal|blue|indigo|purple|pink
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type WhatsappTag = typeof whatsappTags.$inferSelect;

export const whatsappConversationTags = pgTable("whatsapp_conversation_tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  phone: text("phone").notNull(), // canonical phone (digits only, no + prefix)
  tagId: varchar("tag_id").notNull().references(() => whatsappTags.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniquePhoneTag: uniqueIndex("whatsapp_conv_tags_phone_tag_idx").on(table.phone, table.tagId),
}));

// ===== Social / Follow System =====

// Follow requests with approval gate — students control who follows them
export const userFollows = pgTable("user_follows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  followerId: varchar("follower_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  followingId: varchar("following_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"), // "pending" | "accepted"
  createdAt: timestamp("created_at").defaultNow().notNull(),
  acceptedAt: timestamp("accepted_at"),
}, (table) => ({
  uniqueFollowIdx: uniqueIndex("user_follows_follower_following_idx").on(table.followerId, table.followingId),
  followingStatusIdx: index("user_follows_following_status_idx").on(table.followingId, table.status),
}));

export const insertUserFollowSchema = createInsertSchema(userFollows).omit({ id: true, createdAt: true });
export type UserFollow = typeof userFollows.$inferSelect;
export type InsertUserFollow = z.infer<typeof insertUserFollowSchema>;

// Activity feed — append-only log of social events for each user
export const activityFeed = pgTable("activity_feed", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // who performed the action
  activityType: text("activity_type").notNull(),
  // Types: badge_earned | challenge_won | challenge_lost | challenge_completed
  //        streak_milestone | level_up | correct_milestone | new_follower
  payload: jsonb("payload").notNull().default("{}"),
  // badge_earned:        { badgeType, badgeName }
  // challenge_won/lost:  { challengeId, opponentId, opponentName, myScore, opponentScore }
  // streak_milestone:    { streakDays }
  // level_up:            { newLevel }
  // correct_milestone:   { totalCorrect }
  // new_follower:        { followerId, followerName }
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  activityUserIdx: index("activity_feed_user_id_idx").on(table.userId),
  activityCreatedIdx: index("activity_feed_created_at_idx").on(table.createdAt),
}));

export const insertActivityFeedSchema = createInsertSchema(activityFeed).omit({ id: true, createdAt: true });
export type ActivityFeedItem = typeof activityFeed.$inferSelect;
export type InsertActivityFeedItem = z.infer<typeof insertActivityFeedSchema>;

// Feed reactions — one emoji reaction per user per feed item
export const feedReactions = pgTable("feed_reactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  feedItemId: varchar("feed_item_id").notNull().references(() => activityFeed.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  emoji: text("emoji").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqReaction: unique().on(table.feedItemId, table.userId),
}));
export const insertFeedReactionSchema = createInsertSchema(feedReactions).omit({ id: true, createdAt: true });
export type FeedReaction = typeof feedReactions.$inferSelect;
export type InsertFeedReaction = z.infer<typeof insertFeedReactionSchema>;

// ===== User Feedback (Suggestions & Bug Reports) =====
export const userFeedback = pgTable("user_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // "suggestion" | "bug_report"
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  status: text("status").notNull().default("new"), // "new" | "reviewed" | "done"
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  feedbackUserIdx: index("user_feedback_user_id_idx").on(table.userId),
  feedbackCreatedIdx: index("user_feedback_created_at_idx").on(table.createdAt),
}));

export const insertUserFeedbackSchema = createInsertSchema(userFeedback).omit({ id: true, createdAt: true, status: true });
export type UserFeedbackItem = typeof userFeedback.$inferSelect;
export type InsertUserFeedback = z.infer<typeof insertUserFeedbackSchema>;

// ===== App Announcements ("What's New" modal) =====
export const appAnnouncements = pgTable("app_announcements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  subtitle: text("subtitle").notNull().default(""),
  features: jsonb("features").notNull().default([]), // {emoji, title, description}[]
  isActive: boolean("is_active").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertAppAnnouncementSchema = createInsertSchema(appAnnouncements).omit({ id: true, createdAt: true });
export type AppAnnouncement = typeof appAnnouncements.$inferSelect;
export type InsertAppAnnouncement = z.infer<typeof insertAppAnnouncementSchema>;

// ===== Partner Voucher System =====

// A batch is one generation run (admin, partner API or internal API): the unit that gets a
// printable PDF sheet and a CSV. Vouchers carry their own duration/kind so single codes and
// legacy partner codes work without a batch.
export const voucherBatches = pgTable("voucher_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  label: text("label").notNull(),
  kind: text("kind").notNull().default("paid"), // "paid" (sold) | "courtesy" (given away)
  durationDays: integer("duration_days").notNull(),
  quantity: integer("quantity").notNull(),
  expiresAt: timestamp("expires_at"), // last day the codes can be redeemed
  note: text("note"),
  source: text("source").notNull().default("admin"), // admin | partner | api
  createdBy: varchar("created_by").references(() => users.id),
  partnerKeyId: varchar("partner_key_id").references(() => apiKeys.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type VoucherBatch = typeof voucherBatches.$inferSelect;

export const vouchers = pgTable("vouchers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(), // normalised: uppercase, no separators
  plan: text("plan").notNull(), // legacy label: "monthly" | "annual" | "custom"; durationDays is authoritative
  durationDays: integer("duration_days"), // days of access the voucher grants
  kind: text("kind").notNull().default("paid"), // "paid" | "courtesy"
  status: text("status").notNull().default("unused"), // "unused" | "redeemed" | "revoked"
  batchId: varchar("batch_id"),
  serial: integer("serial"), // position within the batch, printed on the sheet
  label: text("label"),
  expiresAt: timestamp("expires_at"),
  source: text("source").notNull().default("admin"), // admin | partner | api
  createdBy: varchar("created_by").references(() => users.id),
  partnerKeyId: varchar("partner_key_id").references(() => apiKeys.id), // which partner generated this voucher
  createdAt: timestamp("created_at").defaultNow().notNull(),
  redeemedAt: timestamp("redeemed_at"),
  redeemedBy: varchar("redeemed_by").references(() => users.id),
  revokedAt: timestamp("revoked_at"),
}, (table) => ({
  statusIdx: index("vouchers_status_idx").on(table.status),
  codeIdx: index("vouchers_code_idx").on(table.code),
  partnerIdx: index("vouchers_partner_key_id_idx").on(table.partnerKeyId),
  batchIdx: index("vouchers_batch_idx").on(table.batchId),
}));

export type Voucher = typeof vouchers.$inferSelect;

// Payment Events (tracks every payment attempt for analytics)
export const paymentEvents = pgTable("payment_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  stripeInvoiceId: text("stripe_invoice_id"),
  stripeCustomerId: text("stripe_customer_id"),
  eventType: text("event_type").notNull(), // payment_succeeded, payment_failed
  amount: integer("amount").notNull().default(0), // in cents
  currency: text("currency").default("usd"),
  attemptCount: integer("attempt_count").notNull().default(1),
  subscriptionPlan: text("subscription_plan"), // monthly, yearly
  failureReason: text("failure_reason"), // card_declined, insufficient_funds, etc.
  recovered: boolean("recovered").default(false), // true if this success came after previous failures
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("payment_events_user_id_idx").on(table.userId),
  typeIdx: index("payment_events_event_type_idx").on(table.eventType),
  createdIdx: index("payment_events_created_at_idx").on(table.createdAt),
}));

export type PaymentEvent = typeof paymentEvents.$inferSelect;

// ── Baremetrics-style metrics: Stripe mirrors + derived ledgers (/admin/metrics) ──────────
export const stripeSubscriptionsMirror = pgTable("stripe_subscriptions_mirror", {
  id: text("id").primaryKey(),
  customerId: text("customer_id").notNull(),
  customerEmail: text("customer_email"),
  customerName: text("customer_name"),
  userId: varchar("user_id"),
  status: text("status").notNull(),
  created: timestamp("created").notNull(),
  startDate: timestamp("start_date"),
  trialStart: timestamp("trial_start"),
  trialEnd: timestamp("trial_end"),
  canceledAt: timestamp("canceled_at"),
  endedAt: timestamp("ended_at"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end").notNull().default(false),
  currentPeriodStart: timestamp("current_period_start"),
  currentPeriodEnd: timestamp("current_period_end"),
  paused: boolean("paused").notNull().default(false),
  pauseResumesAt: timestamp("pause_resumes_at"),
  priceId: text("price_id"),
  productName: text("product_name"),
  unitAmount: integer("unit_amount").notNull().default(0),
  quantity: integer("quantity").notNull().default(1),
  currency: text("currency").notNull().default("jmd"),
  interval: text("interval").notNull().default("month"),
  intervalCount: integer("interval_count").notNull().default(1),
  grade: integer("grade"),
  plan: text("plan"),
  discounts: jsonb("discounts").$type<Array<{ percentOff: number | null; amountOff: number | null; duration: string; start: number | null; end: number | null }>>().notNull().default([]),
  cancellationReason: text("cancellation_reason"),
  cancellationComment: text("cancellation_comment"),
  metadata: jsonb("metadata").$type<Record<string, string>>().notNull().default({}),
  syncedAt: timestamp("synced_at").notNull().defaultNow(),
});
export type StripeSubscriptionMirror = typeof stripeSubscriptionsMirror.$inferSelect;

export const stripeInvoicesMirror = pgTable("stripe_invoices_mirror", {
  id: text("id").primaryKey(),
  subscriptionId: text("subscription_id"),
  customerId: text("customer_id"),
  userId: varchar("user_id"),
  status: text("status"),
  billingReason: text("billing_reason"),
  currency: text("currency"),
  amountDue: integer("amount_due").notNull().default(0),
  amountPaid: integer("amount_paid").notNull().default(0),
  amountRemaining: integer("amount_remaining").notNull().default(0),
  subtotal: integer("subtotal").notNull().default(0),
  total: integer("total").notNull().default(0),
  discountAmount: integer("discount_amount").notNull().default(0),
  created: timestamp("created").notNull(),
  paidAt: timestamp("paid_at"),
  periodStart: timestamp("period_start"),
  periodEnd: timestamp("period_end"),
  attemptCount: integer("attempt_count").notNull().default(0),
  nextPaymentAttempt: timestamp("next_payment_attempt"),
  hostedInvoiceUrl: text("hosted_invoice_url"),
  chargeId: text("charge_id"),
  lastFailureCode: text("last_failure_code"),
  lastFailureMessage: text("last_failure_message"),
  normalizedMonthly: integer("normalized_monthly").notNull().default(0),
  lines: jsonb("lines").$type<Array<{ priceId: string | null; amount: number; interval: string | null; intervalCount: number; quantity: number; description: string | null }>>().notNull().default([]),
  syncedAt: timestamp("synced_at").notNull().defaultNow(),
});
export type StripeInvoiceMirror = typeof stripeInvoicesMirror.$inferSelect;

export const stripeChargesMirror = pgTable("stripe_charges_mirror", {
  id: text("id").primaryKey(),
  customerId: text("customer_id"),
  invoiceId: text("invoice_id"),
  userId: varchar("user_id"),
  status: text("status").notNull(),
  amount: integer("amount").notNull().default(0),
  amountRefunded: integer("amount_refunded").notNull().default(0),
  currency: text("currency"),
  created: timestamp("created").notNull(),
  failureCode: text("failure_code"),
  failureMessage: text("failure_message"),
  declineCode: text("decline_code"),
  outcomeType: text("outcome_type"),
  fee: integer("fee").notNull().default(0),
  net: integer("net").notNull().default(0),
  settledAmount: integer("settled_amount").notNull().default(0),
  settledCurrency: text("settled_currency"),
  exchangeRate: doublePrecision("exchange_rate"),
  balanceTransactionId: text("balance_transaction_id"),
  paymentMethodBrand: text("payment_method_brand"),
  paymentMethodLast4: text("payment_method_last4"),
  disputed: boolean("disputed").notNull().default(false),
  syncedAt: timestamp("synced_at").notNull().defaultNow(),
});
export type StripeChargeMirror = typeof stripeChargesMirror.$inferSelect;

export const stripeRefundsMirror = pgTable("stripe_refunds_mirror", {
  id: text("id").primaryKey(),
  chargeId: text("charge_id"),
  customerId: text("customer_id"),
  amount: integer("amount").notNull().default(0),
  currency: text("currency"),
  reason: text("reason"),
  status: text("status"),
  created: timestamp("created").notNull(),
  settledAmount: integer("settled_amount").notNull().default(0),
  feeRefunded: integer("fee_refunded").notNull().default(0),
  syncedAt: timestamp("synced_at").notNull().defaultNow(),
});

export const metricsMrrEvents = pgTable("metrics_mrr_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  occurredAt: timestamp("occurred_at").notNull(),
  type: text("type").notNull(),
  subscriptionId: text("subscription_id").notNull(),
  customerId: text("customer_id").notNull(),
  userId: varchar("user_id"),
  mrrDelta: integer("mrr_delta").notNull().default(0),
  mrrBefore: integer("mrr_before").notNull().default(0),
  mrrAfter: integer("mrr_after").notNull().default(0),
  plan: text("plan"),
  interval: text("interval"),
  grade: integer("grade"),
  reason: text("reason"),
  meta: jsonb("meta").$type<Record<string, unknown>>().notNull().default({}),
});
export type MetricsMrrEvent = typeof metricsMrrEvents.$inferSelect;

export const metricsGoals = pgTable("metrics_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  metric: text("metric").notNull(),
  target: doublePrecision("target").notNull(),
  startValue: doublePrecision("start_value"),
  startsAt: timestamp("starts_at").notNull().defaultNow(),
  dueAt: timestamp("due_at").notNull(),
  label: text("label"),
  achievedAt: timestamp("achieved_at"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type MetricsGoal = typeof metricsGoals.$inferSelect;

export const metricsAnnotations = pgTable("metrics_annotations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  occurredOn: date("occurred_on").notNull(),
  title: text("title").notNull(),
  note: text("note"),
  kind: text("kind").notNull().default("note"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type MetricsAnnotation = typeof metricsAnnotations.$inferSelect;

export const recoveryCases = pgTable("recovery_cases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  invoiceId: text("invoice_id").notNull().unique(),
  subscriptionId: text("subscription_id"),
  customerId: text("customer_id"),
  userId: varchar("user_id"),
  amount: integer("amount").notNull().default(0),
  currency: text("currency"),
  failureCode: text("failure_code"),
  failureMessage: text("failure_message"),
  payUrl: text("pay_url"),
  status: text("status").notNull().default("open"),
  openedAt: timestamp("opened_at").notNull().defaultNow(),
  nextReminderAt: timestamp("next_reminder_at"),
  reminders: jsonb("reminders").$type<Array<{ at: string; channel: string; step: number; ok: boolean; error?: string }>>().notNull().default([]),
  recoveredAt: timestamp("recovered_at"),
  recoveredAmount: integer("recovered_amount").notNull().default(0),
  closedAt: timestamp("closed_at"),
  closedReason: text("closed_reason"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type RecoveryCase = typeof recoveryCases.$inferSelect;


// Weekly Leaderboard Results (snapshot stored at end of each week)
export const weeklyLeaderboardResults = pgTable("weekly_leaderboard_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  grade: integer("grade").notNull(),
  weekStart: timestamp("week_start").notNull(), // Monday 00:00
  weekEnd: timestamp("week_end").notNull(), // Sunday 23:59
  xpEarned: integer("xp_earned").notNull().default(0),
  questionsAnswered: integer("questions_answered").notNull().default(0),
  correctAnswers: integer("correct_answers").notNull().default(0),
  rank: integer("rank").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Leaderboard Badges (earned from weekly performance)
export const leaderboardBadges = pgTable("leaderboard_badges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  badgeType: text("badge_type").notNull(), // weekly_champion, top3, top10, streak_3, streak_5, champion_5x, champion_10x
  grade: integer("grade").notNull(),
  weekStart: timestamp("week_start"), // which week it was earned (null for milestone badges)
  metadata: jsonb("metadata"), // extra context (streak count, total wins, etc.)
  awardedAt: timestamp("awarded_at").defaultNow().notNull(),
});

export type WeeklyLeaderboardResult = typeof weeklyLeaderboardResults.$inferSelect;
export type LeaderboardBadge = typeof leaderboardBadges.$inferSelect;

// Practice Paper Library & Purchases
export const practicePapers = pgTable("practice_papers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(), // "Grade 6 Mathematics - Paper 1"
  grade: integer("grade").notNull(),
  subject: text("subject").notNull(),
  questionCount: integer("question_count").notNull().default(40),
  topics: jsonb("topics").default("[]"), // topics included
  pdfPath: text("pdf_path").notNull(), // path to stored PDF file
  questionIds: jsonb("question_ids").default("[]"), // question IDs used (for avoiding repeats)
  isActive: boolean("is_active").notNull().default(true),
  paperNumber: integer("paper_number").notNull(), // Paper 1, Paper 2, etc. within grade/subject
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const paperPurchases = pgTable("paper_purchases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }), // null for guest purchases
  email: text("email"), // buyer's email (from Stripe for guests)
  stripeSessionId: text("stripe_session_id"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  downloadToken: text("download_token").unique(), // secure token for guest download links
  packageType: text("package_type").notNull(),
  amountPaid: integer("amount_paid").notNull(),
  papersTotal: integer("papers_total").notNull(),
  papersRemaining: integer("papers_remaining").notNull(),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Short, branded payment links for support: edufocalacademy.com/pay/<code>.
// A Stripe checkout URL is ~600 characters and its #fragment cannot be stripped,
// because the session only resolves client-side from it, so it is unusable in a
// WhatsApp or Instagram message. The row is permanent and the Stripe session is
// not: sessions expire after 24 hours, so /pay/:code mints a fresh one on demand
// rather than handing a parent a dead link days later.
export const paymentLinks = pgTable("payment_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  plan: text("plan").notNull(), // 'monthly' | 'annual'
  priceId: text("price_id").notNull(),
  stripeSessionId: text("stripe_session_id"),
  sessionUrl: text("session_url"),
  sessionExpiresAt: timestamp("session_expires_at"),
  active: boolean("active").notNull().default(true),
  note: text("note"), // why this link was issued, for whoever finds it later
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at"),
  paidAt: timestamp("paid_at"),
});

export const paperDownloads = pgTable("paper_downloads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  purchaseId: varchar("purchase_id").notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }), // null for guests
  paperId: varchar("paper_id").notNull().references(() => practicePapers.id),
  downloadedAt: timestamp("downloaded_at").defaultNow().notNull(),
});

export const guestPaperSelections = pgTable("guest_paper_selections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  purchaseId: varchar("purchase_id").notNull().references(() => paperPurchases.id),
  paperId: varchar("paper_id").notNull().references(() => practicePapers.id),
});

export type PracticePaper = typeof practicePapers.$inferSelect;
export type PaperPurchase = typeof paperPurchases.$inferSelect;
export type PaperDownload = typeof paperDownloads.$inferSelect;
export type GuestPaperSelection = typeof guestPaperSelections.$inferSelect;

// ── App-wide key/value settings (used for runtime feature flags + tunables) ──
export const appSettings = pgTable("app_settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
});

// ── Summer Live Classes ───────────────────────────────────────────────────
// Platform-agnostic — meeting_url accepts any link (Google Meet, Zoom, etc).
// recording_url is set manually by an admin OR by a future Drive-poll job.
export const liveClasses = pgTable("live_classes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(),                      // 4 / 5 / 6
  subject: text("subject"),                               // optional, free text
  title: text("title").notNull(),
  description: text("description"),
  teacherName: text("teacher_name").notNull(),
  scheduledAt: timestamp("scheduled_at").notNull(),
  durationMinutes: integer("duration_minutes").default(60).notNull(),
  meetingUrl: text("meeting_url"),                        // can be Meet/Zoom/etc, set by admin
  topicId: varchar("topic_id").references(() => topics.id, { onDelete: "set null" }), // for post-class quiz
  quizQuestionCount: integer("quiz_question_count"),      // null = use global default
  status: text("status").notNull().default("upcoming"),   // upcoming | live | ended | cancelled
  recordingUrl: text("recording_url"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
}, (table) => ({
  gradeScheduledIdx: index("live_classes_grade_scheduled_idx").on(table.grade, table.scheduledAt),
  statusIdx: index("live_classes_status_idx").on(table.status),
}));

export const classRegistrations = pgTable("class_registrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  classId: varchar("class_id").notNull().references(() => liveClasses.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  registeredAt: timestamp("registered_at").defaultNow().notNull(),
  reminderDayBeforeSentAt: timestamp("reminder_day_before_sent_at"),
  joinedAt: timestamp("joined_at"),                       // when user clicked Join Class
}, (table) => ({
  uniq: uniqueIndex("class_registrations_class_user_uniq").on(table.classId, table.userId),
  userIdx: index("class_registrations_user_idx").on(table.userId),
}));

export const classQuizAssignments = pgTable("class_quiz_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  classId: varchar("class_id").notNull().references(() => liveClasses.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  questionIds: jsonb("question_ids").$type<string[]>().notNull(),
  assignedAt: timestamp("assigned_at").defaultNow().notNull(),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  score: integer("score"),                                // 0-100, percent
  correctCount: integer("correct_count"),
  totalCount: integer("total_count"),
}, (table) => ({
  uniq: uniqueIndex("class_quiz_assignments_class_user_uniq").on(table.classId, table.userId),
  userIdx: index("class_quiz_assignments_user_idx").on(table.userId),
}));

export type AppSetting = typeof appSettings.$inferSelect;
export type LiveClass = typeof liveClasses.$inferSelect;
export type InsertLiveClass = typeof liveClasses.$inferInsert;
export type ClassRegistration = typeof classRegistrations.$inferSelect;
export type ClassQuizAssignment = typeof classQuizAssignments.$inferSelect;

// ── ManyChat flow completion log (analytics + dedup) ─────────────────────
// One row per /api/webhooks/manychat-flow-completed call. Lets us see the
// drop-off between flow-finished and payment-completed without polling
// ManyChat. Joins on email when the user later signs up.
export const manychatFlowCompletions = pgTable("manychat_flow_completions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subscriberId: text("subscriber_id"),
  flowName: text("flow_name"),
  childName: text("child_name"),
  childGrade: integer("child_grade"),
  parentEmail: text("parent_email"),
  parentPhone: text("parent_phone"),
  utmSource: text("utm_source"),
  utmCampaign: text("utm_campaign"),
  rawPayload: jsonb("raw_payload"),
  matchedUserId: varchar("matched_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  emailIdx: index("manychat_flow_completions_email_idx").on(table.parentEmail),
  subscriberIdx: index("manychat_flow_completions_subscriber_idx").on(table.subscriberId),
}));

export type ManychatFlowCompletion = typeof manychatFlowCompletions.$inferSelect;

// ── OpenEMIS integration ────────────────────────────────────────────────
// Academy exposes a Moodle-web-service-compatible endpoint at
// POST /api/openemis/webservice/rest/server.php so an OpenEMIS instance
// (Ministry of Education, school district, single school) can auto-
// provision students, staff, and guardians on Academy using the same
// integration adapter OpenEMIS already ships for Moodle. Each OpenEMIS
// instance authenticates with a per-connection wstoken.
export const openemisConnections = pgTable("openemis_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  wstoken: text("wstoken").notNull().unique(),
  baseUrl: text("base_url"),
  active: boolean("active").default(true).notNull(),
  defaultGrade: integer("default_grade"),
  autoActivateSubscription: boolean("auto_activate_subscription").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at"),
}, (t) => ({
  activeIdx: index("openemis_connections_active_idx").on(t.active),
}));

export const openemisApiLog = pgTable("openemis_api_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: varchar("connection_id").references(() => openemisConnections.id, { onDelete: "set null" }),
  wsfunction: text("wsfunction").notNull(),
  status: text("status").notNull(),
  httpStatus: integer("http_status"),
  params: jsonb("params"),
  response: jsonb("response"),
  errorMessage: text("error_message"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  wsfunctionIdx: index("openemis_api_log_wsfunction_idx").on(t.wsfunction),
  createdAtIdx: index("openemis_api_log_created_at_idx").on(t.createdAt),
  connectionIdx: index("openemis_api_log_connection_idx").on(t.connectionId),
}));

export type OpenemisConnection = typeof openemisConnections.$inferSelect;
export type InsertOpenemisConnection = typeof openemisConnections.$inferInsert;
export type OpenemisApiLog = typeof openemisApiLog.$inferSelect;
export type InsertOpenemisApiLog = typeof openemisApiLog.$inferInsert;


// ==================== INTERACTIVE LESSONS ====================
// Block-based interactive lessons (explain / worked example / checks such as
// sort, order, match, cloze, tap-the-word, number line, fraction shading).
// Block shapes are validated by shared/lessonBlocks.ts.
export const lessons = pgTable("lessons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  grade: integer("grade").notNull(), // 4, 5, 6
  subject: text("subject").notNull(), // canonical subject name, e.g. "Mathematics"
  topic: text("topic").notNull(), // matches topics.name
  description: text("description"),
  emoji: text("emoji"),
  blocks: jsonb("blocks").notNull().$type<unknown[]>(),
  estimatedMinutes: integer("estimated_minutes").notNull().default(8),
  xpReward: integer("xp_reward").notNull().default(150),
  sortOrder: integer("sort_order").notNull().default(0),
  tags: jsonb("tags").default("[]").$type<string[]>(),
  status: text("status").notNull().default("draft"), // draft, published, archived
  generationMethod: text("generation_method").notNull().default("manual"), // manual, ai, import
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  gradeIdx: index("lessons_grade_idx").on(table.grade),
  subjectIdx: index("lessons_subject_idx").on(table.subject),
  topicIdx: index("lessons_topic_idx").on(table.topic),
  statusIdx: index("lessons_status_idx").on(table.status),
}));

export const lessonProgress = pgTable("lesson_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lessonId: varchar("lesson_id").notNull().references(() => lessons.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("not_started"), // not_started, in_progress, completed
  currentBlock: integer("current_block").notNull().default(0), // index of the block to resume at
  // { [blockId]: { correct: boolean, attempts: number } } for check blocks
  results: jsonb("results").notNull().default("{}").$type<Record<string, { correct: boolean; attempts: number }>>(),
  score: integer("score").notNull().default(0), // checks correct on first try
  totalChecks: integer("total_checks").notNull().default(0),
  xpAwarded: integer("xp_awarded").notNull().default(0),
  totalTimeSeconds: integer("total_time_seconds").notNull().default(0),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  lastActivityAt: timestamp("last_activity_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("lesson_progress_user_idx").on(table.userId),
  lessonIdx: index("lesson_progress_lesson_idx").on(table.lessonId),
  userLessonUnique: uniqueIndex("lesson_progress_user_lesson_unique").on(table.userId, table.lessonId),
}));

export const lessonsRelations = relations(lessons, ({ one, many }) => ({
  createdBy: one(users, { fields: [lessons.createdBy], references: [users.id] }),
  progress: many(lessonProgress),
}));

export const lessonProgressRelations = relations(lessonProgress, ({ one }) => ({
  user: one(users, { fields: [lessonProgress.userId], references: [users.id] }),
  lesson: one(lessons, { fields: [lessonProgress.lessonId], references: [lessons.id] }),
}));

export const insertLessonSchema = createInsertSchema(lessons).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateLessonSchema = createInsertSchema(lessons).omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export const insertLessonProgressSchema = createInsertSchema(lessonProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type Lesson = typeof lessons.$inferSelect;
// Use Drizzle's own insert inference: drizzle-zod turns the jsonb columns into unusable array types.
export type InsertLesson = typeof lessons.$inferInsert;
export type UpdateLesson = Partial<Omit<typeof lessons.$inferInsert, "id" | "createdBy" | "createdAt">>;
export type LessonProgress = typeof lessonProgress.$inferSelect;
export type InsertLessonProgress = typeof lessonProgress.$inferInsert;

// ==================== TOPIC NORMS (population benchmarks) ====================
// How students historically perform on each curriculum topic, built from the legacy
// archive (legacy_users.summary.topics) blended with live user_progress. Feeds
// percentiles, mastery estimates, the study plan and the PEP predictor.
export const topicNorms = pgTable("topic_norms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(),
  subject: text("subject").notNull(), // canonical subject (shared/curriculum.ts)
  topicKey: text("topic_key").notNull(), // shared/curriculum.ts topicKey()
  topicDisplay: text("topic_display").notNull(),
  students: integer("students").notNull().default(0),
  legacyStudents: integer("legacy_students").notNull().default(0),
  liveStudents: integer("live_students").notNull().default(0),
  meanAccuracy: real("mean_accuracy").notNull().default(0),
  medianAccuracy: real("median_accuracy").notNull().default(0),
  p25Accuracy: real("p25_accuracy").notNull().default(0),
  p75Accuracy: real("p75_accuracy").notNull().default(0),
  histogram: jsonb("histogram").notNull().default("[]").$type<number[]>(), // 20 bins of 5%
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  gradeSubjectTopicUnique: uniqueIndex("topic_norms_grade_subject_topic_unique").on(table.grade, table.subject, table.topicKey),
}));

export type TopicNorm = typeof topicNorms.$inferSelect;
export type InsertTopicNorm = typeof topicNorms.$inferInsert;

// ==================== DATA MOAT: calibration, misconceptions, prerequisites, retention, outcomes ====================

// 2PL item response theory parameters per question (fitted on the legacy archive, refreshed from live data).
export const itemParams = pgTable("item_params", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }),
  legacyQuestionId: integer("legacy_question_id"),
  a: real("a").notNull().default(1), // discrimination
  b: real("b").notNull().default(0), // difficulty (logits, 0 = average Jamaican student)
  pValue: real("p_value").notNull().default(0.5), // classical proportion correct
  pointBiserial: real("point_biserial").notNull().default(0),
  responses: integer("responses").notNull().default(0),
  flag: text("flag").notNull().default("ok"), // ok, weak-discrimination, bad-key-or-ambiguous, too-easy, too-hard
  source: text("source").notNull().default("legacy"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  questionUnique: uniqueIndex("item_params_question_unique").on(table.questionId),
  flagIdx: index("item_params_flag_idx").on(table.flag),
}));

// Which wrong option students choose, per question (misconception map).
export const questionDistractors = pgTable("question_distractors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }),
  optionId: text("option_id").notNull(), // Academy option id (a, b, c, ...)
  optionText: text("option_text"),
  picks: integer("picks").notNull().default(0),
  responses: integer("responses").notNull().default(0), // all responses to the question
  shareOfWrong: real("share_of_wrong").notNull().default(0), // picks / wrong answers
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  questionOptionUnique: uniqueIndex("question_distractors_question_option_unique").on(table.questionId, table.optionId),
}));

// Learned prerequisite structure: mastery of prereq lifts early success on topic.
export const topicPrerequisites = pgTable("topic_prerequisites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(),
  subject: text("subject").notNull(),
  prereqTopicKey: text("prereq_topic_key").notNull(),
  prereqTopicDisplay: text("prereq_topic_display").notNull(),
  topicKey: text("topic_key").notNull(),
  topicDisplay: text("topic_display").notNull(),
  lift: real("lift").notNull(), // early accuracy when prereq strong minus when weak
  precedence: real("precedence").notNull(), // share of students who met prereq first
  support: integer("support").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  pairUnique: uniqueIndex("topic_prerequisites_pair_unique").on(table.grade, table.subject, table.prereqTopicKey, table.topicKey),
}));

// Learned forgetting curve per topic.
export const topicRetention = pgTable("topic_retention", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grade: integer("grade").notNull(),
  subject: text("subject").notNull(),
  topicKey: text("topic_key").notNull(),
  topicDisplay: text("topic_display").notNull(),
  halfLifeDays: real("half_life_days").notNull(),
  maxDrop: real("max_drop").notNull().default(0),
  samples: integer("samples").notNull().default(0),
  accByGap: jsonb("acc_by_gap").default("{}").$type<Record<string, number>>(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  topicUnique: uniqueIndex("topic_retention_topic_unique").on(table.grade, table.subject, table.topicKey),
}));

// School and parish benchmarks (aggregates only, minimum group sizes enforced at build time).
export const schoolStats = pgTable("school_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  schoolName: text("school_name").notNull(),
  parish: text("parish"),
  grade: integer("grade").notNull(),
  students: integer("students").notNull().default(0),
  answers: integer("answers").notNull().default(0),
  accuracy: real("accuracy").notNull().default(0),
  source: text("source").notNull().default("legacy"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  schoolGradeUnique: uniqueIndex("school_stats_school_grade_source_unique").on(table.schoolName, table.grade, table.source),
  parishIdx: index("school_stats_parish_idx").on(table.parish),
}));

// Current ability estimate per student on the legacy IRT scale (theta ~ N(0,1) across Jamaican students).
export const studentAbility = pgTable("student_ability", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  theta: real("theta").notNull().default(0),
  se: real("se").notNull().default(1),
  responses: integer("responses").notNull().default(0), // calibrated responses used
  percentile: integer("percentile").notNull().default(50),
  source: text("source").notNull().default("live"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userUnique: uniqueIndex("student_ability_user_unique").on(table.userId),
}));

// Real exam outcomes (the label the predictor has never had). Collected with consent from
// parents/students, schools (OpenEMIS) or admin import.
export const pepOutcomes = pgTable("pep_outcomes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  legacyId: integer("legacy_id"),
  examYear: integer("exam_year").notNull(),
  examGrade: integer("exam_grade").notNull().default(6), // 4 = Grade 4 Literacy/Numeracy, 5 = Performance Task, 6 = PEP exit
  placementSchool: text("placement_school"),
  // { mathematics: { level: "Proficient", score: 78 }, languageArts: {...}, science, socialStudies, abilityTest, overall }
  subjects: jsonb("subjects").notNull().default("{}").$type<Record<string, { level?: string | null; score?: number | null }>>(),
  reportedBy: text("reported_by").notNull().default("parent"), // parent, student, school, admin
  consent: boolean("consent").notNull().default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("pep_outcomes_user_idx").on(table.userId),
  yearIdx: index("pep_outcomes_year_idx").on(table.examYear),
}));

export type ItemParams = typeof itemParams.$inferSelect;
export type QuestionDistractor = typeof questionDistractors.$inferSelect;
export type TopicPrerequisite = typeof topicPrerequisites.$inferSelect;
export type TopicRetention = typeof topicRetention.$inferSelect;
export type SchoolStat = typeof schoolStats.$inferSelect;
export type StudentAbility = typeof studentAbility.$inferSelect;
export type PepOutcome = typeof pepOutcomes.$inferSelect;
export type InsertPepOutcome = typeof pepOutcomes.$inferInsert;

// ==================== LEARNING BEYOND SCHOOL: learner profiles, courses, credentials ====================

// Persisted learner model: interests, goals, learning style signals, current strengths/weaknesses and trend.
export const learnerProfiles = pgTable("learner_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  interests: jsonb("interests").notNull().default("[]").$type<string[]>(),
  goals: text("goals"),
  stage: text("stage").notNull().default("primary"), // primary (<= grade 6), secondary (7-11), beyond
  learningStyle: jsonb("learning_style").$type<{
    avgResponseSeconds: number | null; preferredTime: string | null; avgSessionMinutes: number | null;
    sessionsPerWeek: number | null; lessonShare: number | null;
  } | null>(),
  strengths: jsonb("strengths").notNull().default("[]").$type<Array<{ subject: string; topic: string; mastery: number; percentile: number | null }>>(),
  weaknesses: jsonb("weaknesses").notNull().default("[]").$type<Array<{ subject: string; topic: string; mastery: number; percentile: number | null }>>(),
  trend: text("trend").notNull().default("new"), // accelerating, steady, slipping, new
  abilityPercentile: integer("ability_percentile"),
  averageMastery: integer("average_mastery"),
  computedAt: timestamp("computed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userUnique: uniqueIndex("learner_profiles_user_unique").on(table.userId),
}));

// Point-in-time knowledge state, taken daily for active students: the trajectory behind "accelerating".
export const masterySnapshots = pgTable("mastery_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  takenAt: timestamp("taken_at").defaultNow().notNull(),
  grade: integer("grade").notNull(),
  averageMastery: integer("average_mastery"),
  averagePercentile: integer("average_percentile"),
  abilityPercentile: integer("ability_percentile"),
  abilityTheta: real("ability_theta"),
  topicsMastered: integer("topics_mastered").notNull().default(0),
  topicsPractised: integer("topics_practised").notNull().default(0),
  topics: jsonb("topics").notNull().default("[]").$type<Array<{ subject: string; topicKey: string; topic: string; mastery: number; percentile: number | null; band: string }>>(),
}, (table) => ({
  userTakenIdx: index("mastery_snapshots_user_taken_idx").on(table.userId, table.takenAt),
}));

// A course is an ordered sequence of lessons with a credential at the end. School courses map to the
// curriculum; interest courses are for learning beyond school (any grade).
export const courses = pgTable("courses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  description: text("description").notNull().default(""),
  emoji: text("emoji"),
  coverImageUrl: text("cover_image_url"),
  category: text("category").notNull().default("interest"), // school, interest
  interestTags: jsonb("interest_tags").notNull().default("[]").$type<string[]>(),
  subject: text("subject"),
  gradeMin: integer("grade_min"),
  gradeMax: integer("grade_max"),
  level: text("level").notNull().default("starter"), // starter, explorer, expert
  estimatedMinutes: integer("estimated_minutes").notNull().default(45),
  credentialTitle: text("credential_title"),
  prerequisites: jsonb("prerequisites").notNull().default("[]").$type<string[]>(), // course slugs
  sortOrder: integer("sort_order").notNull().default(0),
  status: text("status").notNull().default("draft"), // draft, pending_review, changes_requested, published, archived
  reviewNote: text("review_note"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  creatorName: text("creator_name"),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  statusIdx: index("courses_status_idx").on(table.status),
  categoryIdx: index("courses_category_idx").on(table.category),
}));

export const courseModules = pgTable("course_modules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  courseId: varchar("course_id").notNull().references(() => courses.id, { onDelete: "cascade" }),
  lessonId: varchar("lesson_id").notNull().references(() => lessons.id, { onDelete: "cascade" }),
  sortOrder: integer("sort_order").notNull().default(0),
  required: boolean("required").notNull().default(true),
}, (table) => ({
  courseLessonUnique: uniqueIndex("course_modules_course_lesson_unique").on(table.courseId, table.lessonId),
  courseIdx: index("course_modules_course_idx").on(table.courseId),
  lessonIdx: index("course_modules_lesson_idx").on(table.lessonId),
}));

export const courseEnrollments = pgTable("course_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  courseId: varchar("course_id").notNull().references(() => courses.id, { onDelete: "cascade" }),
  enrolledAt: timestamp("enrolled_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  completedLessons: integer("completed_lessons").notNull().default(0),
  totalLessons: integer("total_lessons").notNull().default(0),
  lastActivityAt: timestamp("last_activity_at"),
  credentialId: varchar("credential_id"),
}, (table) => ({
  userCourseUnique: uniqueIndex("course_enrollments_user_course_unique").on(table.userId, table.courseId),
  courseIdx: index("course_enrollments_course_idx").on(table.courseId),
}));

export const credentials = pgTable("credentials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(), // public verification code
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  courseId: varchar("course_id").notNull().references(() => courses.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  issuedAt: timestamp("issued_at").defaultNow().notNull(),
  lessonsCompleted: integer("lessons_completed").notNull().default(0),
  averageScore: integer("average_score"),
  snapshot: jsonb("snapshot").default("{}").$type<Record<string, unknown>>(),
}, (table) => ({
  userIdx: index("credentials_user_idx").on(table.userId),
}));

// One-time codes that let the native app open the web lesson player already signed in.
export const webHandoffCodes = pgTable("web_handoff_codes", {
  code: text("code").primaryKey(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  redirect: text("redirect").notNull().default("/"),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
});

export type LearnerProfile = typeof learnerProfiles.$inferSelect;
export type MasterySnapshot = typeof masterySnapshots.$inferSelect;
export type Course = typeof courses.$inferSelect;
export type InsertCourse = typeof courses.$inferInsert;
export type CourseModule = typeof courseModules.$inferSelect;
export type CourseEnrollment = typeof courseEnrollments.$inferSelect;
export type Credential = typeof credentials.$inferSelect;

// ==================== COMMS (in-house Brevo replacement): contacts, segments, campaigns, automations ====================

export const commsContacts = pgTable("comms_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }), // the student this contact is about
  kind: text("kind").notNull().default("student"), // student, parent, lead
  email: text("email"),
  phone: text("phone"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  emailStatus: text("email_status").notNull().default("subscribed"), // subscribed, unsubscribed, bounced, complained, invalid
  smsStatus: text("sms_status").notNull().default("subscribed"),
  whatsappStatus: text("whatsapp_status").notNull().default("subscribed"),
  lastEmailAt: timestamp("last_email_at"),
  lastWhatsappAt: timestamp("last_whatsapp_at"),
  lastSmsAt: timestamp("last_sms_at"),
  lastEngagedAt: timestamp("last_engaged_at"),
  source: text("source").notNull().default("users"),
  // Free-form facts for leads (grade, school, legacy ids...) and tags for lists/history ("paid", "trial", "signup_attempt").
  attributes: jsonb("attributes").notNull().default("{}").$type<Record<string, unknown>>(),
  tags: jsonb("tags").notNull().default("[]").$type<string[]>(),
  // consent + preferences
  marketingOptIn: boolean("marketing_opt_in").notNull().default(true),
  marketingOptInAt: timestamp("marketing_opt_in_at"),
  preferences: jsonb("preferences").notNull().default("{}").$type<{ channels?: Record<string, boolean>; frequency?: "normal" | "weekly" | "minimal"; topics?: Record<string, boolean> }>(),
  optInStatus: text("opt_in_status").notNull().default("none"), // leads: none, pending, confirmed
  optInSentAt: timestamp("opt_in_sent_at"),
  optInConfirmedAt: timestamp("opt_in_confirmed_at"),
  pushStatus: text("push_status").notNull().default("subscribed"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  userKindUnique: uniqueIndex("comms_contacts_user_kind_unique").on(t.userId, t.kind),
  emailIdx: index("comms_contacts_email_idx").on(t.email),
  phoneIdx: index("comms_contacts_phone_idx").on(t.phone),
}));

export const commsSuppressions = pgTable("comms_suppressions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  channel: text("channel").notNull(), // email, sms, whatsapp
  address: text("address").notNull(),
  reason: text("reason").notNull(), // bounce, complaint, unsubscribe, manual, invalid
  source: text("source"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  channelAddressUnique: uniqueIndex("comms_suppressions_channel_address_unique").on(t.tenant, t.channel, t.address),
}));

// One row per student with everything a segment can filter on; rebuilt nightly and on demand.
export const commsFacts = pgTable("comms_facts", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  email: text("email"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  grade: integer("grade"),
  school: text("school"),
  role: text("role"),
  isTestAccount: boolean("is_test_account").notNull().default(false),
  subscriptionStatus: text("subscription_status"),
  daysToSubscriptionEnd: integer("days_to_subscription_end"),
  signupDays: integer("signup_days"),
  lastActiveDays: integer("last_active_days"),
  lastActiveAt: timestamp("last_active_at"),
  streakDays: integer("streak_days").notNull().default(0),
  xpLast7d: integer("xp_last_7d").notNull().default(0),
  attemptsLast7d: integer("attempts_last_7d").notNull().default(0),
  attemptsLast30d: integer("attempts_last_30d").notNull().default(0),
  abilityPercentile: integer("ability_percentile"),
  trend: text("trend"),
  averageMastery: integer("average_mastery"),
  weakestSubject: text("weakest_subject"),
  weakestTopic: text("weakest_topic"),
  weakTopicsCount: integer("weak_topics_count").notNull().default(0),
  reviewDueCount: integer("review_due_count").notNull().default(0),
  coursesEnrolled: integer("courses_enrolled").notNull().default(0),
  coursesCompleted: integer("courses_completed").notNull().default(0),
  enrolledCourseSlugs: jsonb("enrolled_course_slugs").notNull().default("[]").$type<string[]>(),
  completedCourseSlugs: jsonb("completed_course_slugs").notNull().default("[]").$type<string[]>(),
  stalledCourseSlug: text("stalled_course_slug"),
  pepOutcomeReported: boolean("pep_outcome_reported").notNull().default(false),
  interests: jsonb("interests").notNull().default("[]").$type<string[]>(),
  parentEmail: text("parent_email"),
  parentPhone: text("parent_phone"),
  parentFirstName: text("parent_first_name"),
  preferredHour: integer("preferred_hour"), // Jamaica local hour the child usually practises, from the learner profile
  refreshedAt: timestamp("refreshed_at").defaultNow().notNull(),
}, (t) => ({
  gradeIdx: index("comms_facts_grade_idx").on(t.grade),
  lastActiveIdx: index("comms_facts_last_active_idx").on(t.lastActiveDays),
}));

export const commsSegments = pgTable("comms_segments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  description: text("description"),
  audience: text("audience").notNull().default("parents"), // parents, students, both
  match: text("match").notNull().default("all"), // all, any
  rules: jsonb("rules").notNull().default("[]").$type<Array<{ field: string; op: string; value?: unknown }>>(),
  lastCount: integer("last_count"),
  lastCountAt: timestamp("last_count_at"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const commsTemplates = pgTable("comms_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  channel: text("channel").notNull().default("email"), // email, whatsapp, sms
  category: text("category").notNull().default("learning"), // learning (product), marketing
  subject: text("subject"),
  previewText: text("preview_text"),
  body: text("body").notNull().default(""), // plain text with {{variables}}; blank line = paragraph, **bold**, [text](url)
  ctaText: text("cta_text"),
  ctaUrl: text("cta_url"),
  whatsappContentSid: text("whatsapp_content_sid"), // approved Twilio Content Template for sends outside the 24h window
  whatsappVariables: jsonb("whatsapp_variables").notNull().default("[]").$type<string[]>(), // ordered variable names -> {{1}}, {{2}}
  pushUrl: text("push_url"), // deep link for push / in-app
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const commsCampaigns = pgTable("comms_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  channel: text("channel").notNull().default("email"),
  category: text("category").notNull().default("marketing"),
  segmentId: varchar("segment_id").references(() => commsSegments.id, { onDelete: "set null" }),
  templateId: varchar("template_id").references(() => commsTemplates.id, { onDelete: "set null" }),
  status: text("status").notNull().default("draft"), // draft, scheduled, sending, paused, sent, cancelled
  scheduledAt: timestamp("scheduled_at"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  audienceCount: integer("audience_count").notNull().default(0),
  stats: jsonb("stats").notNull().default("{}").$type<Record<string, number>>(),
  holdoutPercent: integer("holdout_percent").notNull().default(0),
  abSubjects: jsonb("ab_subjects").notNull().default("[]").$type<string[]>(),
  sendTimeOptimized: boolean("send_time_optimized").notNull().default(false),
  conversionGoal: text("conversion_goal").notNull().default("login"), // login, lesson, renewal, signup, none
  conversionWindowDays: integer("conversion_window_days").notNull().default(7),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  statusIdx: index("comms_campaigns_status_idx").on(t.status),
}));

export const commsAutomations = pgTable("comms_automations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  trigger: text("trigger").notNull(), // signup_welcome, inactive_7d, streak_at_risk, review_due, subscription_lapsing, credential_earned, child_accelerating, pep_results_window, course_stalled
  channel: text("channel").notNull().default("email"),
  category: text("category").notNull().default("learning"),
  audience: text("audience").notNull().default("parents"),
  templateId: varchar("template_id").references(() => commsTemplates.id, { onDelete: "set null" }),
  enabled: boolean("enabled").notNull().default(false),
  capPerContactDays: integer("cap_per_contact_days").notNull().default(3),
  repeatEveryDays: integer("repeat_every_days"), // null = once per contact
  stats: jsonb("stats").notNull().default("{}").$type<Record<string, number>>(),
  holdoutPercent: integer("holdout_percent").notNull().default(0),
  sendTimeOptimized: boolean("send_time_optimized").notNull().default(false),
  conversionGoal: text("conversion_goal").notNull().default("login"),
  conversionWindowDays: integer("conversion_window_days").notNull().default(7),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const commsAutomationRuns = pgTable("comms_automation_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  automationId: varchar("automation_id").notNull().references(() => commsAutomations.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => commsContacts.id, { onDelete: "cascade" }),
  triggerKey: text("trigger_key").notNull(),
  sendId: varchar("send_id"),
  status: text("status").notNull().default("queued"), // queued, sent, skipped
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  runUnique: uniqueIndex("comms_automation_runs_unique").on(t.automationId, t.contactId, t.triggerKey),
}));

export const commsSends = pgTable("comms_sends", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  campaignId: varchar("campaign_id").references(() => commsCampaigns.id, { onDelete: "cascade" }),
  automationId: varchar("automation_id").references(() => commsAutomations.id, { onDelete: "cascade" }),
  journeyId: varchar("journey_id"),
  journeyStepId: varchar("journey_step_id"),
  contactId: varchar("contact_id").references(() => commsContacts.id, { onDelete: "set null" }),
  userId: varchar("user_id"),
  channel: text("channel").notNull(),
  address: text("address").notNull(),
  subject: text("subject"),
  status: text("status").notNull().default("queued"), // queued, sending, sent, delivered, bounced, complained, failed, suppressed, cancelled
  scheduledFor: timestamp("scheduled_for").defaultNow().notNull(),
  providerMessageId: text("provider_message_id"),
  provider: text("provider"),
  error: text("error"),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  variables: jsonb("variables").default("{}").$type<Record<string, string>>(),
  category: text("category").notNull().default("learning"), // learning, marketing, transactional, digest
  holdout: boolean("holdout").notNull().default(false), // control group: never sent, tracked for uplift
  convertedAt: timestamp("converted_at"),
  conversionType: text("conversion_type"),
  abVariant: text("ab_variant"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  campaignIdx: index("comms_sends_campaign_idx").on(t.campaignId),
  statusScheduledIdx: index("comms_sends_status_scheduled_idx").on(t.status, t.scheduledFor),
  providerMsgIdx: index("comms_sends_provider_msg_idx").on(t.providerMessageId),
  addressSentIdx: index("comms_sends_address_sent_idx").on(t.address, t.sentAt),
}));

export const commsEvents = pgTable("comms_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  sendId: varchar("send_id").references(() => commsSends.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id"),
  channel: text("channel").notNull(),
  type: text("type").notNull(), // delivered, bounce, complaint, open, click, unsubscribe, failed, reply
  provider: text("provider"),
  url: text("url"),
  payload: jsonb("payload").default("{}").$type<Record<string, unknown>>(),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
}, (t) => ({
  sendIdx: index("comms_events_send_idx").on(t.sendId),
  typeTimeIdx: index("comms_events_type_time_idx").on(t.type, t.occurredAt),
}));

// Static lists (imports, legacy audiences, signup attempts) and their members.
export const commsLists = pgTable("comms_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  description: text("description"),
  kind: text("kind").notNull().default("static"),
  source: text("source"),
  memberCount: integer("member_count").notNull().default(0),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const commsListMembers = pgTable("comms_list_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  listId: varchar("list_id").notNull().references(() => commsLists.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => commsContacts.id, { onDelete: "cascade" }),
  source: text("source"),
  addedAt: timestamp("added_at").defaultNow().notNull(),
}, (t) => ({
  listContactUnique: uniqueIndex("comms_list_members_unique").on(t.listId, t.contactId),
  contactIdx: index("comms_list_members_contact_idx").on(t.contactId),
}));

// Multi-step sequences: a trigger (segment membership, list membership or a code event) enrols a
// contact; steps fire after a delay, each optionally conditioned on the previous step's engagement.
export const commsJourneys = pgTable("comms_journeys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenant: text("tenant").notNull().default("academy"),
  name: text("name").notNull(),
  description: text("description"),
  trigger: text("trigger").notNull().default("segment"), // segment, list, event
  segmentId: varchar("segment_id").references(() => commsSegments.id, { onDelete: "set null" }),
  listId: varchar("list_id").references(() => commsLists.id, { onDelete: "set null" }),
  eventKey: text("event_key"),
  audience: text("audience").notNull().default("parents"),
  channel: text("channel").notNull().default("email"),
  category: text("category").notNull().default("learning"),
  enabled: boolean("enabled").notNull().default(false),
  exitWhenUnmatched: boolean("exit_when_unmatched").notNull().default(true),
  exitWhenConverted: boolean("exit_when_converted").notNull().default(true),
  reEnrollAfterDays: integer("re_enroll_after_days"),
  respectCap: boolean("respect_cap").notNull().default(false),
  stats: jsonb("stats").notNull().default("{}").$type<Record<string, number | string | null>>(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const commsJourneySteps = pgTable("comms_journey_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  journeyId: varchar("journey_id").notNull().references(() => commsJourneys.id, { onDelete: "cascade" }),
  position: integer("position").notNull(),
  name: text("name"),
  delayHours: integer("delay_hours").notNull().default(24),
  templateId: varchar("template_id").references(() => commsTemplates.id, { onDelete: "set null" }),
  channel: text("channel"),
  condition: text("condition").notNull().default("always"), // always, if_not_opened, if_not_clicked, if_opened, if_clicked
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  journeyIdx: index("comms_journey_steps_journey_idx").on(t.journeyId, t.position),
}));

export const commsJourneyEnrollments = pgTable("comms_journey_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  journeyId: varchar("journey_id").notNull().references(() => commsJourneys.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => commsContacts.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),
  address: text("address").notNull(),
  channel: text("channel").notNull(),
  nextPosition: integer("next_position").notNull().default(1),
  status: text("status").notNull().default("active"), // active, completed, exited
  nextRunAt: timestamp("next_run_at"),
  lastSendId: varchar("last_send_id"),
  exitReason: text("exit_reason"),
  variables: jsonb("variables").notNull().default("{}").$type<Record<string, string>>(),
  enteredAt: timestamp("entered_at").defaultNow().notNull(),
  finishedAt: timestamp("finished_at"),
}, (t) => ({
  dueIdx: index("comms_journey_enrollments_due_idx").on(t.status, t.nextRunAt),
  contactIdx: index("comms_journey_enrollments_contact_idx").on(t.contactId),
}));

export type CommsList = typeof commsLists.$inferSelect;
export type CommsJourney = typeof commsJourneys.$inferSelect;
export type CommsJourneyStep = typeof commsJourneySteps.$inferSelect;
export type CommsJourneyEnrollment = typeof commsJourneyEnrollments.$inferSelect;
export type CommsContact = typeof commsContacts.$inferSelect;
export type CommsSegment = typeof commsSegments.$inferSelect;
export type CommsTemplate = typeof commsTemplates.$inferSelect;
export type CommsCampaign = typeof commsCampaigns.$inferSelect;
export type CommsAutomation = typeof commsAutomations.$inferSelect;
export type CommsSend = typeof commsSends.$inferSelect;
export type CommsFacts = typeof commsFacts.$inferSelect;

// ============================================================
// PEP Performance Tasks (see shared/performanceTasks.ts for the document shape)
// ============================================================

export const performanceTasks = pgTable("performance_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  grade: integer("grade").notNull(), // 4, 5, 6
  subject: text("subject").notNull(),
  // Which examination and paper this belongs to. PEP tasks predate these and leave them null.
  exam: text("exam"), // PEP | CSEC | CAPE
  paper: text("paper"), // paper1 | paper2 | paper3
  module: integer("module"), // CAPE only: which of the Unit's three modules // Mathematics, Language Arts, Science, Social Studies
  theme: text("theme"),
  description: text("description").notNull(),
  document: jsonb("document").notNull().$type<Record<string, unknown>>(), // full TaskDocument incl. keys
  timeMinutes: integer("time_minutes").notNull().default(90),
  xpReward: integer("xp_reward").notNull().default(300),
  totalPoints: real("total_points").notNull().default(0),
  itemCount: integer("item_count").notNull().default(0),
  status: text("status").notNull().default("draft"), // draft, published, archived
  source: text("source").notNull().default("manual"), // seed, ai, manual
  sortOrder: integer("sort_order").notNull().default(0),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  gradeIdx: index("performance_tasks_grade_idx").on(table.grade),
  statusIdx: index("performance_tasks_status_idx").on(table.status),
}));

export const performanceTaskAttempts = pgTable("performance_task_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").notNull().references(() => performanceTasks.id, { onDelete: "cascade" }),
  attemptNumber: integer("attempt_number").notNull().default(1),
  // Set by the mobile app when a task is started offline: the app addresses the attempt by
  // this id until it has synced, and a replayed start returns the same row.
  clientAttemptId: text("client_attempt_id"),
  // in_progress -> submitted (objective marked, marker pending) -> graded | needs_review (AI failed, admin marks)
  status: text("status").notNull().default("in_progress"),
  currentPart: integer("current_part").notNull().default(0),
  responses: jsonb("responses").notNull().default("{}").$type<Record<string, unknown>>(),
  notes: jsonb("notes").notNull().default("{}").$type<Record<string, string>>(), // important points per source (unscored)
  itemScores: jsonb("item_scores").notNull().default("{}").$type<Record<string, unknown>>(),
  score: real("score"),
  maxScore: real("max_score").notNull().default(0),
  percent: real("percent"),
  level: text("level"), // Beginning, Developing, Proficient, Highly Proficient
  aiFeedback: jsonb("ai_feedback").$type<{ summary: string; strengths: string[]; improvements: string[]; nextSteps?: string[] } | null>(),
  gradingProvider: text("grading_provider"), // anthropic:<model>, openai:<model>, admin:<userId>, auto
  gradingError: text("grading_error"),
  gradingAttempts: integer("grading_attempts").notNull().default(0),
  nextGradeAt: timestamp("next_grade_at"),
  xpAwarded: integer("xp_awarded").notNull().default(0),
  timeSpentSeconds: integer("time_spent_seconds").notNull().default(0),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  submittedAt: timestamp("submitted_at"),
  gradedAt: timestamp("graded_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("pt_attempts_user_idx").on(table.userId),
  taskIdx: index("pt_attempts_task_idx").on(table.taskId),
  statusIdx: index("pt_attempts_status_idx").on(table.status),
}));

export type PerformanceTask = typeof performanceTasks.$inferSelect;
export type InsertPerformanceTask = typeof performanceTasks.$inferInsert;
export type PerformanceTaskAttempt = typeof performanceTaskAttempts.$inferSelect;
export type InsertPerformanceTaskAttempt = typeof performanceTaskAttempts.$inferInsert;

// ============================================================
// Lead acquisition: the free PEP Readiness Check.
// A parent or student can take a short diagnostic with no account. It hooks them with real value
// (a marked report and a national band), and it collects the data that makes the funnel work:
// grade, subject weakness, real ability, which offer they saw, and whether they converted.
// ============================================================

export const leadChecks = pgTable("lead_checks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Anonymous until the email step, so the funnel can measure drop-off before capture.
  contactId: varchar("contact_id").references(() => commsContacts.id, { onDelete: "set null" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  email: text("email"),
  firstName: text("first_name"),
  grade: integer("grade").notNull(),
  status: text("status").notNull().default("started"), // started, answered, captured, reported, converted
  // Per-question record: [{ questionId, subject, topic, correct, ms }]
  answers: jsonb("answers").notNull().default("[]").$type<Array<Record<string, unknown>>>(),
  score: integer("score").notNull().default(0),
  total: integer("total").notNull().default(0),
  percent: real("percent"),
  band: text("band"), // Beginning, Developing, Proficient, Highly Proficient
  percentile: real("percentile"), // national position from the moat norms
  strongestSubject: text("strongest_subject"),
  weakestSubject: text("weakest_subject"),
  weakestTopic: text("weakest_topic"),
  // Attribution and offer, so we can price and prove what works.
  source: text("source"), // email, whatsapp, social, direct
  campaignId: varchar("campaign_id"),
  offerCode: text("offer_code"), // the discount/trial code shown
  offerShownAt: timestamp("offer_shown_at"),
  offerClickedAt: timestamp("offer_clicked_at"),
  convertedAt: timestamp("converted_at"),
  subscriptionId: text("subscription_id"),
  utm: jsonb("utm").notNull().default("{}").$type<Record<string, string>>(),
  userAgent: text("user_agent"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  contactIdx: index("lead_checks_contact_idx").on(t.contactId),
  emailIdx: index("lead_checks_email_idx").on(t.email),
  statusIdx: index("lead_checks_status_idx").on(t.status),
  startedIdx: index("lead_checks_started_idx").on(t.startedAt),
}));

export type LeadCheck = typeof leadChecks.$inferSelect;
export type InsertLeadCheck = typeof leadChecks.$inferInsert;

// ============================================================
// Acquisition funnel daily snapshot (/admin/acquisition).
// The live funnel query can always be re-run, but the lead pool and the Stripe mirrors move
// under us. One frozen row per Jamaica day is the only way to answer "what did the funnel look
// like on the day we sent wave 3". Written by the daily job in server/services/comms/funnel.ts.
// ============================================================

export const acquisitionDaily = pgTable("acquisition_daily", {
  day: date("day").primaryKey(),
  leadsTotal: integer("leads_total").notNull().default(0),
  leadsReachable: integer("leads_reachable").notNull().default(0),
  emailsSent: integer("emails_sent").notNull().default(0),
  emailsDelivered: integer("emails_delivered").notNull().default(0),
  emailsOpened: integer("emails_opened").notNull().default(0),
  emailsClicked: integer("emails_clicked").notNull().default(0),
  emailsBounced: integer("emails_bounced").notNull().default(0),
  emailsComplained: integer("emails_complained").notNull().default(0),
  emailsUnsubscribed: integer("emails_unsubscribed").notNull().default(0),
  contactsReached: integer("contacts_reached").notNull().default(0),
  checksStarted: integer("checks_started").notNull().default(0),
  checksCompleted: integer("checks_completed").notNull().default(0),
  checksCaptured: integer("checks_captured").notNull().default(0),
  signups: integer("signups").notNull().default(0),
  signupsAttributed: integer("signups_attributed").notNull().default(0),
  trialsStarted: integer("trials_started").notNull().default(0),
  paidConversions: integer("paid_conversions").notNull().default(0),
  paidAttributed: integer("paid_attributed").notNull().default(0),
  revenueGrossCents: integer("revenue_gross_cents").notNull().default(0),
  revenueRefundsCents: integer("revenue_refunds_cents").notNull().default(0),
  revenueFeesCents: integer("revenue_fees_cents").notNull().default(0),
  revenueNetCents: integer("revenue_net_cents").notNull().default(0),
  revenueAttributedNetCents: integer("revenue_attributed_net_cents").notNull().default(0),
  newMrrCents: integer("new_mrr_cents").notNull().default(0),
  // Per-offer, per-wave, per-grade and per-parish rows for that day, so history survives a re-segment.
  breakdowns: jsonb("breakdowns").notNull().default("{}").$type<Record<string, Array<Record<string, unknown>>>>(),
  computedAt: timestamp("computed_at").defaultNow().notNull(),
});

export type AcquisitionDaily = typeof acquisitionDaily.$inferSelect;
export type InsertAcquisitionDaily = typeof acquisitionDaily.$inferInsert;

// Reading responses: what a student wrote in a reader's "Your turn to write" box, saved as they
// type (web and app), plus optional teacher-style AI feedback on that text.
export const readingResponses = pgTable("reading_responses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  materialId: varchar("material_id").notNull().references(() => readingMaterials.id, { onDelete: "cascade" }),
  promptKey: text("prompt_key").notNull(),
  text: text("text").notNull().default(""),
  feedback: jsonb("feedback"),
  feedbackAt: timestamp("feedback_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userMaterialPromptIdx: uniqueIndex("reading_responses_user_material_prompt_idx").on(table.userId, table.materialId, table.promptKey),
}));
export type ReadingResponse = typeof readingResponses.$inferSelect;

// Kid-level explanations of a wrong answer, cached per (question, chosen answer) so each is generated once.
export const questionExplanations = pgTable("question_explanations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id").notNull().references(() => questions.id, { onDelete: "cascade" }),
  chosenKey: text("chosen_key").notNull().default(""),
  explanation: jsonb("explanation").notNull(),
  plain: text("plain").notNull(),
  provider: text("provider"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  questionChosenIdx: uniqueIndex("question_explanations_question_chosen_idx").on(table.questionId, table.chosenKey),
}));
export type QuestionExplanation = typeof questionExplanations.$inferSelect;

// ---------------------------------------------------------------------------
// Schools (autocomplete for the school field; seeded from the legacy Jamaican list)
// ---------------------------------------------------------------------------
export const schools = pgTable("schools", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  nameKey: text("name_key").notNull(), // normalised for matching: lower-case, "st." -> "saint", no apostrophes, single spaces
  parish: text("parish").notNull().default(""),
  type: text("type"), // Primary | Preparatory | Secondary
  level: text("level"), // GSAT | CXC (legacy labels)
  source: text("source").notNull().default("legacy"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  nameParish: uniqueIndex("schools_name_key_parish_idx").on(table.nameKey, table.parish),
}));
export type School = typeof schools.$inferSelect;

// ---------------------------------------------------------------------------
// Login attempts (why a sign-in failed, without storing what was typed)
// ---------------------------------------------------------------------------
// Until this table existed, a failed sign-in left no trace anywhere: `user.login`
// was only written on success, so "people can't log in and we don't know what they
// used" was literally unanswerable. It records the SHAPE of each attempt, never the
// credential — a SHA-256 of the identifier (so repeat attempts group without the
// value being readable) plus a masked preview a support agent can recognise.
export const loginAttempts = pgTable("login_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  identifierHash: text("identifier_hash").notNull(),   // sha256(lower(trim(identifier)))
  identifierKind: text("identifier_kind").notNull(),   // email | username
  identifierMasked: text("identifier_masked").notNull(), // g•••••@gmail.com / gor•••
  // success | bad_password | no_match | legacy_claimed | legacy_bad_password | rate_limited
  outcome: text("outcome").notNull(),
  // Set when the identifier resolved but something else failed — tells username
  // typing apart from a genuinely wrong password.
  matchedBy: text("matched_by"),                       // email | username | legacy_email | legacy_username
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  surface: text("surface").notNull().default("web"),   // web | mobile | find_account
  ipHash: text("ip_hash"),                             // sha256(ip); for burst detection, not identification
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  createdAtIdx: index("login_attempts_created_at_idx").on(table.createdAt),
  outcomeIdx: index("login_attempts_outcome_idx").on(table.outcome),
  identifierHashIdx: index("login_attempts_identifier_hash_idx").on(table.identifierHash),
}));
export type LoginAttempt = typeof loginAttempts.$inferSelect;

// One-hour links that let a legacy EduFocal student claim their old account by proving
// control of the mailbox on it, instead of by remembering a password from years ago.
// The Academy user is created when the token is redeemed, never when it is issued.
export const legacyClaimTokens = pgTable("legacy_claim_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  token: text("token").notNull().unique(),
  legacyId: integer("legacy_id").notNull().references(() => legacyUsers.legacyId, { onDelete: "cascade" }),
  expiresAt: timestamp("expires_at").notNull(),
  used: boolean("used").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type LegacyClaimToken = typeof legacyClaimTokens.$inferSelect;
